import React, { useState, useEffect, useRef } from 'react';
import { LogOut, Plus, Shield, ShieldAlert, Mail, Tag, CalendarDays, Users, Upload, Download, AlertCircle, CheckCircle, WifiOff, Clock, ClipboardList, Database, Contact, Trophy, MapPin } from 'lucide-react';

// '??' (non '||'): una REACT_APP_API_URL vuota è intenzionale (stesso-origine,
// dietro un reverse proxy che instrada /api e /health al backend) e va
// rispettata, non scambiata per "non impostata".
const API_URL = process.env.REACT_APP_API_URL ?? 'http://localhost:5000';
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';
const APP_VERSION = process.env.REACT_APP_VERSION || 'dev';
const APP_VERSION_LABEL = APP_VERSION.startsWith('v') ? APP_VERSION : `build ${APP_VERSION}`;

// Taglie maglietta ammesse per un atleta, in ordine crescente (campo facoltativo).
// Lista duplicata in backend (validazione) e frontend: se cambia, va aggiornata in tutti e tre.
const TAGLIE = ['S', 'M', 'L', 'XL', 'XXL'];

// Pannello di amministrazione, separato dall'app principale (frontend/) e riservato agli account
// con ruolo admin. Il login (solo Google Sign-In, vedi CLAUDE.md) è lo stesso identico flusso
// dell'app principale, contro lo stesso backend — solo dopo l'autenticazione questa app verifica
// il ruolo e blocca l'accesso a un istruttore autenticato (vedi AccessDeniedScreen): il vero
// confine di sicurezza resta comunque lato backend (`requireAdmin` su ogni route di backoffice),
// questo è solo un filtro UX per non mostrare a un non-admin un pannello che comunque non
// potrebbe usare.
export default function AdminApp() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [darkMode, setDarkMode] = useState(true);
  const [activeTab, setActiveTab] = useState('users'); // users | athletes | whitelist | categories | courseDays | attendance | races | backup

  const [users, setUsers] = useState([]);
  const [editingUser, setEditingUser] = useState(null); // null = chiuso, {} = nuovo, {id,...} = modifica

  const [athletes, setAthletes] = useState([]);
  const [editingAthlete, setEditingAthlete] = useState(null); // null = chiuso, {} = nuovo, {id,...} = modifica
  const [finImportAthlete, setFinImportAthlete] = useState(null); // atleta di cui importare le gare da FIN Veneto
  const [athletesImportResult, setAthletesImportResult] = useState(null);
  const [athletesImporting, setAthletesImporting] = useState(false);
  const [athletesExporting, setAthletesExporting] = useState(false);

  const [whitelist, setWhitelist] = useState([]);
  const [editingWhitelistEntry, setEditingWhitelistEntry] = useState(null);
  const [whitelistImportResult, setWhitelistImportResult] = useState(null);
  const [whitelistImporting, setWhitelistImporting] = useState(false);
  const [whitelistExporting, setWhitelistExporting] = useState(false);

  const [categories, setCategories] = useState([]);
  const [editingCategory, setEditingCategory] = useState(null);

  const [courseDays, setCourseDays] = useState([]);
  const [editingCourseDay, setEditingCourseDay] = useState(null);

  const [impianti, setImpianti] = useState([]);
  const [editingImpianto, setEditingImpianto] = useState(null);

  const [attendanceImportResult, setAttendanceImportResult] = useState(null);
  const [attendanceImporting, setAttendanceImporting] = useState(false);
  const [attendanceExporting, setAttendanceExporting] = useState(false);

  const [dbBackupDownloading, setDbBackupDownloading] = useState(false);
  const [dbRestoreUploading, setDbRestoreUploading] = useState(false);
  const [dbRestoreResult, setDbRestoreResult] = useState(null);

  // Importa dati da produzione (solo sviluppo: il backend dice se è abilitato)
  const [prodPull, setProdPull] = useState({ enabled: false, prodUrl: null });
  const [prodToken, setProdToken] = useState('');
  const [prodPullBackupFirst, setProdPullBackupFirst] = useState(true);
  const [prodPulling, setProdPulling] = useState(false);

  useEffect(() => {
    checkAuth();
  }, []);

  // Dati necessari alla schermata Utenti (categorie/turni assegnabili nel modal) caricati subito
  // dopo il login, solo per un account admin — un istruttore non arriva mai a vedere queste liste.
  useEffect(() => {
    if (user && user.role === 'admin') {
      fetchUsers();
      fetchAthletes();
      fetchCategories();
      fetchCourseDays();
      fetchImpianti();
      fetchProdPullConfig();
    }
  }, [user]);

  const checkAuth = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await fetch(`${API_URL}/api/auth/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
      } else {
        localStorage.removeItem('token');
      }
    } catch (err) {
      console.error('Auth check failed:', err);
    }
  };

  const loginWithGoogle = async (credential) => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/auth/google`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ credential })
      });

      if (response.ok) {
        const { token, user: userData } = await response.json();
        localStorage.setItem('token', token);
        setUser(userData);
      } else {
        const body = await response.json().catch(() => ({}));
        alert(body.error || 'Accesso con Google non autorizzato');
      }
    } catch (err) {
      alert('Errore login con Google: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    setUser(null);
  };

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/users`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setUsers(data);
      }
    } catch (err) {
      console.error('Failed to fetch users:', err);
    }
  };

  const saveUser = async (userData) => {
    try {
      const token = localStorage.getItem('token');
      const isEditing = Boolean(userData.id);
      const response = await fetch(
        `${API_URL}/api/users${isEditing ? `/${userData.id}` : ''}`,
        {
          method: isEditing ? 'PUT' : 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(userData)
        }
      );

      if (response.ok) {
        await fetchUsers();
        setEditingUser(null);
      } else {
        const err = await response.json().catch(() => ({}));
        alert('Errore salvataggio account: ' + (err.error || response.statusText));
      }
    } catch (err) {
      alert('Errore salvataggio account: ' + err.message);
    }
  };

  const deleteUser = async (userId) => {
    if (!window.confirm('Eliminare questo account? L\'operazione non è reversibile.')) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/users/${userId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        await fetchUsers();
      } else {
        const err = await response.json().catch(() => ({}));
        alert('Errore eliminazione account: ' + (err.error || response.statusText));
      }
    } catch (err) {
      alert('Errore eliminazione account: ' + err.message);
    }
  };

  const fetchAthletes = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/athletes`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setAthletes(data);
      }
    } catch (err) {
      console.error('Failed to fetch athletes:', err);
    }
  };

  const saveAthlete = async (athleteData) => {
    try {
      const token = localStorage.getItem('token');
      const isEditing = Boolean(athleteData.id);
      const response = await fetch(
        `${API_URL}/api/athletes${isEditing ? `/${athleteData.id}` : ''}`,
        {
          method: isEditing ? 'PUT' : 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(athleteData)
        }
      );

      if (response.ok) {
        await fetchAthletes();
        setEditingAthlete(null);
      } else {
        const err = await response.json().catch(() => ({}));
        alert('Errore salvataggio atleta: ' + (err.error || response.statusText));
      }
    } catch (err) {
      alert('Errore salvataggio atleta: ' + err.message);
    }
  };

  const deleteAthlete = async (athleteId) => {
    if (!window.confirm('Eliminare questo atleta? L\'operazione non è reversibile.')) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/athletes/${athleteId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        await fetchAthletes();
      } else {
        alert('Errore eliminazione atleta');
      }
    } catch (err) {
      alert('Errore eliminazione atleta: ' + err.message);
    }
  };

  const importAthletesFile = async (file) => {
    if (!file) return;
    try {
      setAthletesImporting(true);
      setAthletesImportResult(null);
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${API_URL}/api/athletes/import`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      const data = await response.json().catch(() => ({}));

      if (response.ok) {
        setAthletesImportResult(data);
        await fetchAthletes();
      } else {
        alert('Errore importazione: ' + (data.error || response.statusText));
      }
    } catch (err) {
      alert('Errore importazione: ' + err.message);
    } finally {
      setAthletesImporting(false);
    }
  };

  const downloadAthletesTemplate = async () => {
    try {
      setAthletesExporting(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/athletes/export`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        alert('Errore scaricamento modello: ' + (err.error || response.statusText));
        return;
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'atleti.xlsx';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert('Errore scaricamento modello: ' + err.message);
    } finally {
      setAthletesExporting(false);
    }
  };

  const fetchWhitelist = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/google-whitelist`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setWhitelist(data);
      }
    } catch (err) {
      console.error('Failed to fetch whitelist:', err);
    }
  };

  const saveWhitelistEntry = async (entryData) => {
    try {
      const token = localStorage.getItem('token');
      const isEditing = Boolean(entryData.id);
      const response = await fetch(
        `${API_URL}/api/google-whitelist${isEditing ? `/${entryData.id}` : ''}`,
        {
          method: isEditing ? 'PUT' : 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(entryData)
        }
      );

      if (response.ok) {
        await fetchWhitelist();
        setEditingWhitelistEntry(null);
      } else {
        const err = await response.json().catch(() => ({}));
        alert('Errore salvataggio whitelist: ' + (err.error || response.statusText));
      }
    } catch (err) {
      alert('Errore salvataggio whitelist: ' + err.message);
    }
  };

  const deleteWhitelistEntry = async (entryId) => {
    if (!window.confirm('Rimuovere questa email dalla whitelist?')) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/google-whitelist/${entryId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        await fetchWhitelist();
      } else {
        const err = await response.json().catch(() => ({}));
        alert('Errore rimozione whitelist: ' + (err.error || response.statusText));
      }
    } catch (err) {
      alert('Errore rimozione whitelist: ' + err.message);
    }
  };

  const importWhitelistFile = async (file) => {
    if (!file) return;
    try {
      setWhitelistImporting(true);
      setWhitelistImportResult(null);
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${API_URL}/api/google-whitelist/import`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      const data = await response.json().catch(() => ({}));

      if (response.ok) {
        setWhitelistImportResult(data);
        await fetchWhitelist();
      } else {
        alert('Errore importazione: ' + (data.error || response.statusText));
      }
    } catch (err) {
      alert('Errore importazione: ' + err.message);
    } finally {
      setWhitelistImporting(false);
    }
  };

  const downloadWhitelistTemplate = async () => {
    try {
      setWhitelistExporting(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/google-whitelist/export`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        alert('Errore scaricamento: ' + (err.error || response.statusText));
        return;
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'google_whitelist.xlsx';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert('Errore scaricamento: ' + err.message);
    } finally {
      setWhitelistExporting(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/categories`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setCategories(data);
      }
    } catch (err) {
      console.error('Failed to fetch categories:', err);
    }
  };

  const saveCategory = async (categoryData) => {
    try {
      const token = localStorage.getItem('token');
      const isEditing = Boolean(categoryData.id);
      const response = await fetch(
        `${API_URL}/api/categories${isEditing ? `/${categoryData.id}` : ''}`,
        {
          method: isEditing ? 'PUT' : 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(categoryData)
        }
      );

      if (response.ok) {
        await Promise.all([fetchCategories(), fetchUsers()]);
        setEditingCategory(null);
      } else {
        const err = await response.json().catch(() => ({}));
        alert('Errore salvataggio categoria: ' + (err.error || response.statusText));
      }
    } catch (err) {
      alert('Errore salvataggio categoria: ' + err.message);
    }
  };

  const deleteCategory = async (categoryId) => {
    if (!window.confirm('Eliminare questa categoria?')) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/categories/${categoryId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        await fetchCategories();
      } else {
        const err = await response.json().catch(() => ({}));
        alert('Errore eliminazione categoria: ' + (err.error || response.statusText));
      }
    } catch (err) {
      alert('Errore eliminazione categoria: ' + err.message);
    }
  };

  const fetchCourseDays = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/course-days`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setCourseDays(data);
      }
    } catch (err) {
      console.error('Failed to fetch course days:', err);
    }
  };

  const saveCourseDay = async (courseDayData) => {
    try {
      const token = localStorage.getItem('token');
      const isEditing = Boolean(courseDayData.id);
      const response = await fetch(
        `${API_URL}/api/course-days${isEditing ? `/${courseDayData.id}` : ''}`,
        {
          method: isEditing ? 'PUT' : 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(courseDayData)
        }
      );

      if (response.ok) {
        await Promise.all([fetchCourseDays(), fetchUsers()]);
        setEditingCourseDay(null);
      } else {
        const err = await response.json().catch(() => ({}));
        alert('Errore salvataggio turno: ' + (err.error || response.statusText));
      }
    } catch (err) {
      alert('Errore salvataggio turno: ' + err.message);
    }
  };

  const deleteCourseDay = async (courseDayId) => {
    if (!window.confirm('Eliminare questo giorno?')) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/course-days/${courseDayId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        await fetchCourseDays();
      } else {
        const err = await response.json().catch(() => ({}));
        alert('Errore eliminazione turno: ' + (err.error || response.statusText));
      }
    } catch (err) {
      alert('Errore eliminazione turno: ' + err.message);
    }
  };

  const fetchImpianti = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/impianti`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        setImpianti(await response.json());
      }
    } catch (err) {
      console.error('Failed to fetch impianti:', err);
    }
  };

  const saveImpianto = async (impiantoData) => {
    try {
      const token = localStorage.getItem('token');
      const isEditing = Boolean(impiantoData.id);
      const response = await fetch(
        `${API_URL}/api/impianti${isEditing ? `/${impiantoData.id}` : ''}`,
        {
          method: isEditing ? 'PUT' : 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(impiantoData)
        }
      );

      if (response.ok) {
        // La rinomina si propaga (FK ON UPDATE CASCADE) ad atleti e istruttori: si ricaricano anche loro
        await Promise.all([fetchImpianti(), fetchUsers(), fetchAthletes()]);
        setEditingImpianto(null);
      } else {
        const err = await response.json().catch(() => ({}));
        alert('Errore salvataggio impianto: ' + (err.error || response.statusText));
      }
    } catch (err) {
      alert('Errore salvataggio impianto: ' + err.message);
    }
  };

  const deleteImpianto = async (impiantoId) => {
    if (!window.confirm('Eliminare questo impianto?')) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/impianti/${impiantoId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        await fetchImpianti();
      } else {
        const err = await response.json().catch(() => ({}));
        alert('Errore eliminazione impianto: ' + (err.error || response.statusText));
      }
    } catch (err) {
      alert('Errore eliminazione impianto: ' + err.message);
    }
  };

  const importAttendanceFile = async (file) => {
    if (!file) return;
    try {
      setAttendanceImporting(true);
      setAttendanceImportResult(null);
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${API_URL}/api/attendance/import`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      const data = await response.json().catch(() => ({}));

      if (response.ok) {
        setAttendanceImportResult(data);
      } else {
        alert('Errore importazione: ' + (data.error || response.statusText));
      }
    } catch (err) {
      alert('Errore importazione: ' + err.message);
    } finally {
      setAttendanceImporting(false);
    }
  };

  const downloadAttendanceExport = async () => {
    try {
      setAttendanceExporting(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/attendance/export`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        alert('Errore scaricamento: ' + (err.error || response.statusText));
        return;
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'presenze.xlsx';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert('Errore scaricamento: ' + err.message);
    } finally {
      setAttendanceExporting(false);
    }
  };

  const downloadDbBackup = async () => {
    try {
      setDbBackupDownloading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/db/dump`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        alert('Errore backup: ' + (err.error || response.statusText));
        return false;
      }

      // Nome file preso dal Content-Disposition del backend (include data/ora), con un fallback
      // generico se per qualche motivo non fosse presente.
      const disposition = response.headers.get('content-disposition') || '';
      const match = disposition.match(/filename="([^"]+)"/);
      const filename = match ? match[1] : 'swimming-backup.sql';

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      return true;
    } catch (err) {
      alert('Errore backup: ' + err.message);
      return false;
    } finally {
      setDbBackupDownloading(false);
    }
  };

  const fetchProdPullConfig = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/db/pull-prod`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setProdPull(await response.json());
      }
    } catch (err) {
      console.error('Failed to fetch prod pull config:', err);
    }
  };

  // Sostituisce il DB di sviluppo con una copia di quello di produzione (solo ambiente dev). Il token
  // di produzione passa dal backend dev alla produzione e non viene salvato da nessuna parte.
  const pullFromProduction = async () => {
    if (!prodToken.trim()) return;
    if (!window.confirm(
      `Il database di SVILUPPO verrà SOSTITUITO con una copia di quello di produzione (${prodPull.prodUrl}). La produzione viene solo letta. Continuare?`
    )) {
      return;
    }
    if (prodPullBackupFirst && !(await downloadDbBackup())) {
      alert('Backup del database di sviluppo non riuscito: importazione annullata.');
      return;
    }

    try {
      setProdPulling(true);
      setDbRestoreResult(null);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/db/pull-prod`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ token: prodToken.trim() })
      });
      const data = await response.json().catch(() => ({}));

      if (response.ok) {
        setProdToken('');
        setDbRestoreResult({
          success: true,
          message: data.message || 'Dati di produzione importati.',
          schemaUpdated: Boolean(data.schemaUpdated),
          counts: data.counts || null,
          oldSchema: Boolean(data.oldSchema),
          fromProduction: true
        });
        await Promise.all([
          fetchUsers(),
          fetchAthletes(),
          fetchCategories(),
          fetchCourseDays(),
          fetchImpianti(),
          fetchWhitelist()
        ]);
      } else {
        setDbRestoreResult({
          success: false,
          message: data.error || `Errore ${response.status}: ${response.statusText}`,
          detail: data.detail
        });
      }
    } catch (err) {
      setDbRestoreResult({ success: false, message: err.message });
    } finally {
      setProdPulling(false);
    }
  };

  const restoreDbBackup = async (file) => {
    if (!file) return;
    if (!window.confirm(
      'ATTENZIONE: il ripristino SOVRASCRIVE completamente il database attuale con il contenuto di questo file — tutti i dati esistenti (atleti, presenze, manifestazioni, gare e tempi, utenti, categorie, turni) andranno persi e sostituiti. Questa operazione non è reversibile. Continuare?'
    )) {
      return;
    }

    try {
      setDbRestoreUploading(true);
      setDbRestoreResult(null);
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${API_URL}/api/admin/db/restore`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      const data = await response.json().catch(() => ({}));

      if (response.ok) {
        // Dopo il restore il backend rilancia le migrazioni (initDatabase): un dump di una versione
        // precedente viene riallineato allo schema corrente, con i campi nuovi (es. taglia) vuoti.
        setDbRestoreResult({
          success: true,
          message: data.message || 'Database ripristinato.',
          schemaUpdated: Boolean(data.schemaUpdated),
          counts: data.counts || null
        });
        // Il database è cambiato per intero: ricarica le liste già caricate così l'admin vede
        // subito i dati ripristinati (le fetch gestiscono da sole i propri errori).
        await Promise.all([
          fetchUsers(),
          fetchAthletes(),
          fetchCategories(),
          fetchCourseDays(),
          fetchImpianti(),
          fetchWhitelist()
        ]);
      } else {
        // Anche un 500 "Database ripristinato, ma aggiornamento dello schema fallito" arriva qui:
        // il messaggio del backend spiega già cosa fare (riavviare il backend).
        setDbRestoreResult({
          success: false,
          message: data.error || `Errore ${response.status}: ${response.statusText}`,
          detail: data.detail
        });
      }
    } catch (err) {
      setDbRestoreResult({ success: false, message: err.message });
    } finally {
      setDbRestoreUploading(false);
    }
  };

  // ========== RENDER ==========

  if (!user) {
    return (
      <LoginScreen
        onGoogleLogin={loginWithGoogle}
        googleClientId={GOOGLE_CLIENT_ID}
        darkMode={darkMode}
        setDarkMode={setDarkMode}
      />
    );
  }

  if (user.role !== 'admin') {
    return <AccessDeniedScreen user={user} onLogout={logout} darkMode={darkMode} />;
  }

  return (
    <div className={`min-h-screen ${darkMode ? 'bg-slate-900 text-white' : 'bg-gray-50 text-slate-900'} pb-24`}>
      {/* Header */}
      <div className={`${darkMode ? 'bg-slate-800' : 'bg-white'} shadow-lg sticky top-0 z-40`}>
        <div className="max-w-4xl mx-auto px-4 py-3 flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Shield size={22} />
              Admin{' '}
              <span className={`text-xs font-normal align-middle ${darkMode ? 'text-slate-500' : 'text-gray-400'}`}>
                {APP_VERSION_LABEL}
              </span>
            </h1>
            <p className="text-xs opacity-60">{user.name}</p>
          </div>
          <button
            onClick={() => setDarkMode(!darkMode)}
            className={`px-3 py-2 rounded-lg transition ${darkMode ? 'bg-slate-700' : 'bg-gray-200'}`}
          >
            {darkMode ? '☀️' : '🌙'}
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-4">
        <div className={`flex gap-0 mb-4 rounded-lg overflow-hidden border flex-wrap ${darkMode ? 'border-slate-700' : 'border-gray-300'}`}>
          <button
            onClick={() => setActiveTab('users')}
            className={`flex-1 py-2.5 px-4 font-medium text-sm flex items-center gap-1.5 justify-center ${
              activeTab === 'users'
                ? 'bg-blue-600 text-white'
                : darkMode ? 'bg-slate-800 hover:bg-slate-700' : 'bg-white hover:bg-gray-100'
            }`}
          >
            <Users size={16} />
            Utenti
          </button>
          <button
            onClick={() => { setActiveTab('athletes'); fetchAthletes(); }}
            className={`flex-1 py-2.5 px-4 font-medium text-sm flex items-center gap-1.5 justify-center ${
              activeTab === 'athletes'
                ? 'bg-blue-600 text-white'
                : darkMode ? 'bg-slate-800 hover:bg-slate-700' : 'bg-white hover:bg-gray-100'
            }`}
          >
            <Contact size={16} />
            Atleti
          </button>
          <button
            onClick={() => { setActiveTab('whitelist'); fetchWhitelist(); }}
            className={`flex-1 py-2.5 px-4 font-medium text-sm flex items-center gap-1.5 justify-center ${
              activeTab === 'whitelist'
                ? 'bg-blue-600 text-white'
                : darkMode ? 'bg-slate-800 hover:bg-slate-700' : 'bg-white hover:bg-gray-100'
            }`}
          >
            <Mail size={16} />
            Whitelist Google
          </button>
          <button
            onClick={() => { setActiveTab('categories'); fetchCategories(); }}
            className={`flex-1 py-2.5 px-4 font-medium text-sm flex items-center gap-1.5 justify-center ${
              activeTab === 'categories'
                ? 'bg-blue-600 text-white'
                : darkMode ? 'bg-slate-800 hover:bg-slate-700' : 'bg-white hover:bg-gray-100'
            }`}
          >
            <Tag size={16} />
            Categorie
          </button>
          <button
            onClick={() => { setActiveTab('courseDays'); fetchCourseDays(); }}
            className={`flex-1 py-2.5 px-4 font-medium text-sm flex items-center gap-1.5 justify-center ${
              activeTab === 'courseDays'
                ? 'bg-blue-600 text-white'
                : darkMode ? 'bg-slate-800 hover:bg-slate-700' : 'bg-white hover:bg-gray-100'
            }`}
          >
            <CalendarDays size={16} />
            Turni
          </button>
          <button
            onClick={() => { setActiveTab('impianti'); fetchImpianti(); }}
            className={`flex-1 py-2.5 px-4 font-medium text-sm flex items-center gap-1.5 justify-center ${
              activeTab === 'impianti'
                ? 'bg-blue-600 text-white'
                : darkMode ? 'bg-slate-800 hover:bg-slate-700' : 'bg-white hover:bg-gray-100'
            }`}
          >
            <MapPin size={16} />
            Impianti
          </button>
          <button
            onClick={() => setActiveTab('attendance')}
            className={`flex-1 py-2.5 px-4 font-medium text-sm flex items-center gap-1.5 justify-center ${
              activeTab === 'attendance'
                ? 'bg-blue-600 text-white'
                : darkMode ? 'bg-slate-800 hover:bg-slate-700' : 'bg-white hover:bg-gray-100'
            }`}
          >
            <ClipboardList size={16} />
            Presenze
          </button>
          <button
            onClick={() => { setActiveTab('races'); fetchAthletes(); }}
            className={`flex-1 py-2.5 px-4 font-medium text-sm flex items-center gap-1.5 justify-center ${
              activeTab === 'races'
                ? 'bg-blue-600 text-white'
                : darkMode ? 'bg-slate-800 hover:bg-slate-700' : 'bg-white hover:bg-gray-100'
            }`}
          >
            <Trophy size={16} />
            Gare
          </button>
          <button
            onClick={() => setActiveTab('backup')}
            className={`flex-1 py-2.5 px-4 font-medium text-sm flex items-center gap-1.5 justify-center ${
              activeTab === 'backup'
                ? 'bg-blue-600 text-white'
                : darkMode ? 'bg-slate-800 hover:bg-slate-700' : 'bg-white hover:bg-gray-100'
            }`}
          >
            <Database size={16} />
            Backup
          </button>
        </div>

        {activeTab === 'users' && (
          <div>
            <button
              onClick={() => setEditingUser({})}
              className="w-full mb-4 bg-blue-600 hover:bg-blue-700 text-white px-4 py-3 rounded-lg flex items-center gap-2 justify-center"
            >
              <Plus size={20} />
              Aggiungi Account
            </button>
            <div className="space-y-3">
              {users.length === 0 ? (
                <p className="text-center py-8 opacity-50">Nessun account trovato</p>
              ) : (
                users.map(u => (
                  <div key={u.id} className={`p-4 rounded-lg ${darkMode ? 'bg-slate-700' : 'bg-gray-100'}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="font-semibold">{u.username}</p>
                        {u.email && <p className="text-sm opacity-60">{u.email}</p>}
                        <div className="flex flex-wrap gap-2 mt-1">
                          <span className={`text-xs px-2 py-0.5 rounded-full ${darkMode ? 'bg-blue-900/40 text-blue-300' : 'bg-blue-100 text-blue-700'}`}>
                            {u.role === 'admin' ? 'Amministratore' : 'Istruttore'}
                          </span>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${
                            u.active
                              ? (darkMode ? 'bg-green-900/40 text-green-300' : 'bg-green-100 text-green-700')
                              : (darkMode ? 'bg-red-900/40 text-red-300' : 'bg-red-100 text-red-700')
                          }`}>
                            {u.active ? 'Attivo' : 'Disattivato'}
                          </span>
                          {u.categorie && u.categorie.map(c => (
                            <span key={c} className={`text-xs px-2 py-0.5 rounded-full ${darkMode ? 'bg-purple-900/40 text-purple-300' : 'bg-purple-100 text-purple-700'}`}>
                              {c}
                            </span>
                          ))}
                          {u.turni && u.turni.map(t => (
                            <span key={t} className={`text-xs px-2 py-0.5 rounded-full ${darkMode ? 'bg-amber-900/40 text-amber-300' : 'bg-amber-100 text-amber-700'}`}>
                              {t.charAt(0).toUpperCase() + t.slice(1)}
                            </span>
                          ))}
                          {u.impianti && u.impianti.map(i => (
                            <span key={i} className={`text-xs px-2 py-0.5 rounded-full ${darkMode ? 'bg-teal-900/40 text-teal-300' : 'bg-teal-100 text-teal-700'}`}>
                              {i}
                            </span>
                          ))}
                        </div>
                      </div>
                      <div className="flex gap-2 shrink-0">
                        <button
                          onClick={() => setEditingUser(u)}
                          className={`px-3 py-2 rounded-lg text-sm ${darkMode ? 'bg-slate-600 hover:bg-slate-500' : 'bg-gray-300 hover:bg-gray-400'}`}
                        >
                          Modifica
                        </button>
                        <button
                          onClick={() => deleteUser(u.id)}
                          className="px-3 py-2 rounded-lg text-sm bg-red-600 hover:bg-red-700 text-white"
                        >
                          Elimina
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {activeTab === 'athletes' && (
          <div>
            <div className="flex flex-col sm:flex-row gap-2 mb-4">
              <button
                onClick={() => setEditingAthlete({})}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-4 py-3 rounded-lg flex items-center gap-2 justify-center"
              >
                <Plus size={20} />
                Aggiungi Atleta
              </button>
              <label
                className={`flex-1 cursor-pointer px-4 py-3 rounded-lg flex items-center gap-2 justify-center font-medium ${
                  darkMode ? 'bg-slate-700 hover:bg-slate-600' : 'bg-gray-200 hover:bg-gray-300'
                } ${athletesImporting ? 'opacity-60 cursor-not-allowed' : ''}`}
              >
                <Upload size={20} />
                {athletesImporting ? 'Importazione...' : 'Importa da Excel'}
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  disabled={athletesImporting}
                  onChange={(e) => {
                    const file = e.target.files && e.target.files[0];
                    importAthletesFile(file);
                    e.target.value = '';
                  }}
                  className="hidden"
                />
              </label>
              <button
                onClick={downloadAthletesTemplate}
                disabled={athletesExporting}
                className={`flex-1 px-4 py-3 rounded-lg flex items-center gap-2 justify-center font-medium text-white ${
                  darkMode ? 'bg-emerald-700 hover:bg-emerald-600' : 'bg-emerald-600 hover:bg-emerald-700'
                } ${athletesExporting ? 'opacity-60 cursor-not-allowed' : ''}`}
              >
                <Download size={20} />
                {athletesExporting ? 'Download...' : 'Scarica modello Excel'}
              </button>
            </div>

            {athletesImportResult && (
              <div className={`mb-4 p-4 rounded-lg ${darkMode ? 'bg-slate-700' : 'bg-gray-100'}`}>
                <p className="font-semibold mb-1">Risultato importazione</p>
                <p className="text-sm">Importati: {athletesImportResult.imported} — Aggiornati: {athletesImportResult.updated ?? 0} — Saltati: {athletesImportResult.skipped}</p>
                {athletesImportResult.errors && athletesImportResult.errors.length > 0 && (
                  <div className="mt-2 space-y-1 max-h-64 overflow-y-auto">
                    {athletesImportResult.errors.map((e, idx) => (
                      <p key={idx} className={`text-xs ${darkMode ? 'text-amber-300' : 'text-amber-700'}`}>
                        Riga {e.row}: {e.reason}
                      </p>
                    ))}
                  </div>
                )}
                <button
                  onClick={() => setAthletesImportResult(null)}
                  className={`mt-3 px-3 py-1.5 rounded-lg text-xs ${darkMode ? 'bg-slate-600 hover:bg-slate-500' : 'bg-gray-300 hover:bg-gray-400'}`}
                >
                  Chiudi
                </button>
              </div>
            )}

            <div className="space-y-3">
              {athletes.length === 0 ? (
                <p className="text-center py-8 opacity-50">Nessun atleta trovato</p>
              ) : (
                athletes.map(athlete => (
                  <div key={athlete.id} className={`p-4 rounded-lg ${darkMode ? 'bg-slate-700' : 'bg-gray-100'}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="font-semibold">{athlete.nome} {athlete.cognome}</p>
                        {athlete.email && <p className="text-sm opacity-60">{athlete.email}</p>}
                        {athlete.telefono && <p className="text-sm opacity-60">{athlete.telefono}</p>}
                        {athlete.data_nascita && (
                          <p className="text-sm opacity-60">
                            Nato/a il {new Date(athlete.data_nascita).toLocaleDateString('it-IT')}
                          </p>
                        )}
                        <span className={`inline-block mt-1 text-xs px-2 py-0.5 rounded-full ${darkMode ? 'bg-purple-900/40 text-purple-300' : 'bg-purple-100 text-purple-700'}`}>
                          {athlete.categoria}
                        </span>
                        {athlete.taglia && (
                          <span className={`inline-block mt-1 ml-1 text-xs px-2 py-0.5 rounded-full ${darkMode ? 'bg-sky-900/40 text-sky-300' : 'bg-sky-100 text-sky-700'}`}>
                            Taglia {athlete.taglia}
                          </span>
                        )}
                        {(athlete.impianti || []).map(nome => (
                          <span key={nome} className={`inline-block mt-1 ml-1 text-xs px-2 py-0.5 rounded-full ${darkMode ? 'bg-indigo-900/40 text-indigo-300' : 'bg-indigo-100 text-indigo-700'}`}>
                            {nome}
                          </span>
                        ))}
                        {athlete.codice_tessera_fin && (
                          <span className={`inline-block mt-1 ml-1 text-xs px-2 py-0.5 rounded-full ${darkMode ? 'bg-teal-900/40 text-teal-300' : 'bg-teal-100 text-teal-700'}`}>
                            Tessera FIN {athlete.codice_tessera_fin}
                          </span>
                        )}
                        {athlete.id_fin && (
                          <span className={`inline-block mt-1 ml-1 text-xs px-2 py-0.5 rounded-full ${darkMode ? 'bg-teal-900/40 text-teal-300' : 'bg-teal-100 text-teal-700'}`}>
                            ID FIN {athlete.id_fin}
                          </span>
                        )}
                      </div>
                      <div className="flex flex-wrap justify-end gap-2 shrink-0">
                        {/^\d+$/.test(athlete.id_fin || '') && (
                          <button
                            onClick={() => setFinImportAthlete(athlete)}
                            title="Importa manifestazioni e tempi dalla scheda atleta FIN Veneto"
                            className="px-3 py-2 rounded-lg text-sm bg-teal-600 hover:bg-teal-700 text-white flex items-center gap-1"
                          >
                            <Download size={14} /> Gare FIN
                          </button>
                        )}
                        <button
                          onClick={() => setEditingAthlete(athlete)}
                          className={`px-3 py-2 rounded-lg text-sm ${darkMode ? 'bg-slate-600 hover:bg-slate-500' : 'bg-gray-300 hover:bg-gray-400'}`}
                        >
                          Modifica
                        </button>
                        <button
                          onClick={() => deleteAthlete(athlete.id)}
                          className="px-3 py-2 rounded-lg text-sm bg-red-600 hover:bg-red-700 text-white"
                        >
                          Elimina
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {activeTab === 'whitelist' && (
          <div>
            <div className="flex flex-col sm:flex-row gap-2 mb-4">
              <button
                onClick={() => setEditingWhitelistEntry({})}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-4 py-3 rounded-lg flex items-center gap-2 justify-center"
              >
                <Plus size={20} />
                Aggiungi Email
              </button>
              <label
                className={`flex-1 cursor-pointer px-4 py-3 rounded-lg flex items-center gap-2 justify-center font-medium ${
                  darkMode ? 'bg-slate-700 hover:bg-slate-600' : 'bg-gray-200 hover:bg-gray-300'
                } ${whitelistImporting ? 'opacity-60 cursor-not-allowed' : ''}`}
              >
                <Upload size={20} />
                {whitelistImporting ? 'Importazione...' : 'Importa da Excel'}
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  disabled={whitelistImporting}
                  onChange={(e) => {
                    const file = e.target.files && e.target.files[0];
                    importWhitelistFile(file);
                    e.target.value = '';
                  }}
                  className="hidden"
                />
              </label>
              <button
                onClick={downloadWhitelistTemplate}
                disabled={whitelistExporting}
                className={`flex-1 px-4 py-3 rounded-lg flex items-center gap-2 justify-center font-medium text-white ${
                  darkMode ? 'bg-emerald-700 hover:bg-emerald-600' : 'bg-emerald-600 hover:bg-emerald-700'
                } ${whitelistExporting ? 'opacity-60 cursor-not-allowed' : ''}`}
              >
                <Download size={20} />
                {whitelistExporting ? 'Download...' : 'Esporta Excel'}
              </button>
            </div>

            {whitelistImportResult && (
              <div className={`mb-4 p-4 rounded-lg ${darkMode ? 'bg-slate-700' : 'bg-gray-100'}`}>
                <p className="font-semibold mb-1">Risultato importazione</p>
                <p className="text-sm">Importati: {whitelistImportResult.imported} — Saltati: {whitelistImportResult.skipped}</p>
                {whitelistImportResult.errors && whitelistImportResult.errors.length > 0 && (
                  <div className="mt-2 space-y-1">
                    {whitelistImportResult.errors.map((e, idx) => (
                      <p key={idx} className={`text-xs ${darkMode ? 'text-amber-300' : 'text-amber-700'}`}>
                        Riga {e.row}: {e.reason}
                      </p>
                    ))}
                  </div>
                )}
                <button
                  onClick={() => setWhitelistImportResult(null)}
                  className={`mt-3 px-3 py-1.5 rounded-lg text-xs ${darkMode ? 'bg-slate-600 hover:bg-slate-500' : 'bg-gray-300 hover:bg-gray-400'}`}
                >
                  Chiudi
                </button>
              </div>
            )}

            <p className={`text-xs mb-3 ${darkMode ? 'text-slate-500' : 'text-gray-400'}`}>
              Un'email qui presente crea automaticamente il proprio account, col ruolo indicato, al primo accesso con Google.
            </p>

            <div className="space-y-3">
              {whitelist.length === 0 ? (
                <p className="text-center py-8 opacity-50">Nessuna email in whitelist</p>
              ) : (
                whitelist.map(w => (
                  <div key={w.id} className={`p-4 rounded-lg ${darkMode ? 'bg-slate-700' : 'bg-gray-100'}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="font-semibold">{w.email}</p>
                        <span className={`inline-block mt-1 text-xs px-2 py-0.5 rounded-full ${darkMode ? 'bg-blue-900/40 text-blue-300' : 'bg-blue-100 text-blue-700'}`}>
                          {w.role === 'admin' ? 'Amministratore' : 'Istruttore'}
                        </span>
                      </div>
                      <div className="flex gap-2 shrink-0">
                        <button
                          onClick={() => setEditingWhitelistEntry(w)}
                          className={`px-3 py-2 rounded-lg text-sm ${darkMode ? 'bg-slate-600 hover:bg-slate-500' : 'bg-gray-300 hover:bg-gray-400'}`}
                        >
                          Modifica
                        </button>
                        <button
                          onClick={() => deleteWhitelistEntry(w.id)}
                          className="px-3 py-2 rounded-lg text-sm bg-red-600 hover:bg-red-700 text-white"
                        >
                          Elimina
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {activeTab === 'categories' && (
          <div>
            <button
              onClick={() => setEditingCategory({})}
              className="w-full mb-4 bg-blue-600 hover:bg-blue-700 text-white px-4 py-3 rounded-lg flex items-center gap-2 justify-center"
            >
              <Plus size={20} />
              Aggiungi Categoria
            </button>

            <p className={`text-xs mb-3 ${darkMode ? 'text-slate-500' : 'text-gray-400'}`}>
              Le categorie determinano cosa può gestire ogni atleta e cosa vede ogni istruttore in fase di appello (sezione Utenti). I turni assegnati a una categoria determinano il totale sessioni mensili nelle statistiche. Una categoria in uso da atleti o istruttori non può essere eliminata.
            </p>

            <div className="space-y-3">
              {categories.length === 0 ? (
                <p className="text-center py-8 opacity-50">Nessuna categoria trovata</p>
              ) : (
                categories.map(c => (
                  <div key={c.id} className={`p-4 rounded-lg flex items-center justify-between gap-3 ${darkMode ? 'bg-slate-700' : 'bg-gray-100'}`}>
                    <div>
                      <p className="font-semibold">{c.nome}</p>
                      <p className={`text-xs mt-0.5 ${darkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                        {c.giorni && c.giorni.length > 0
                          ? c.giorni.map(g => g.charAt(0).toUpperCase() + g.slice(1)).join(', ')
                          : 'Nessun turno assegnato'}
                      </p>
                    </div>
                    <div className="flex gap-2 shrink-0">
                      <button
                        onClick={() => setEditingCategory(c)}
                        className={`px-3 py-2 rounded-lg text-sm ${darkMode ? 'bg-slate-600 hover:bg-slate-500' : 'bg-gray-300 hover:bg-gray-400'}`}
                      >
                        Modifica
                      </button>
                      <button
                        onClick={() => deleteCategory(c.id)}
                        className="px-3 py-2 rounded-lg text-sm bg-red-600 hover:bg-red-700 text-white"
                      >
                        Elimina
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {activeTab === 'courseDays' && (
          <div>
            <button
              onClick={() => setEditingCourseDay({})}
              className="w-full mb-4 bg-blue-600 hover:bg-blue-700 text-white px-4 py-3 rounded-lg flex items-center gap-2 justify-center"
            >
              <Plus size={20} />
              Aggiungi Turno
            </button>

            <p className={`text-xs mb-3 ${darkMode ? 'text-slate-500' : 'text-gray-400'}`}>
              I turni sono i giorni della settimana selezionabili in fase di appello e assegnabili a ogni istruttore (sezione Utenti). Un giorno in uso da presenze o istruttori non può essere eliminato.
            </p>

            <div className="space-y-3">
              {courseDays.length === 0 ? (
                <p className="text-center py-8 opacity-50">Nessun turno trovato</p>
              ) : (
                courseDays.map(d => (
                  <div key={d.id} className={`p-4 rounded-lg flex items-center justify-between gap-3 ${darkMode ? 'bg-slate-700' : 'bg-gray-100'}`}>
                    <p className="font-semibold">{d.nome.charAt(0).toUpperCase() + d.nome.slice(1)}</p>
                    <div className="flex gap-2 shrink-0">
                      <button
                        onClick={() => setEditingCourseDay(d)}
                        className={`px-3 py-2 rounded-lg text-sm ${darkMode ? 'bg-slate-600 hover:bg-slate-500' : 'bg-gray-300 hover:bg-gray-400'}`}
                      >
                        Modifica
                      </button>
                      <button
                        onClick={() => deleteCourseDay(d.id)}
                        className="px-3 py-2 rounded-lg text-sm bg-red-600 hover:bg-red-700 text-white"
                      >
                        Elimina
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {activeTab === 'impianti' && (
          <div>
            <button
              onClick={() => setEditingImpianto({})}
              className="w-full mb-4 bg-blue-600 hover:bg-blue-700 text-white px-4 py-3 rounded-lg flex items-center gap-2 justify-center"
            >
              <Plus size={20} />
              Aggiungi Impianto
            </button>

            <p className={`text-xs mb-3 ${darkMode ? 'text-slate-500' : 'text-gray-400'}`}>
              Gli impianti (piscine) si assegnano agli istruttori (sezione Utenti) e agli atleti: in fase di appello un istruttore vede solo gli atleti del proprio impianto e delle proprie categorie. Rinominare un impianto aggiorna automaticamente atleti, istruttori e presenze; un impianto in uso non può essere eliminato.
            </p>

            <div className="space-y-3">
              {impianti.length === 0 ? (
                <p className="text-center py-8 opacity-50">Nessun impianto trovato</p>
              ) : (
                impianti.map(i => (
                  <div key={i.id} className={`p-4 rounded-lg flex items-center justify-between gap-3 ${darkMode ? 'bg-slate-700' : 'bg-gray-100'}`}>
                    <div>
                      <p className="font-semibold">{i.nome}</p>
                      <p className={`text-xs mt-0.5 ${darkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                        {athletes.filter(a => (a.impianti || []).includes(i.nome)).length} atleti · {users.filter(u => (u.impianti || []).includes(i.nome)).length} account
                      </p>
                    </div>
                    <div className="flex gap-2 shrink-0">
                      <button
                        onClick={() => setEditingImpianto(i)}
                        className={`px-3 py-2 rounded-lg text-sm ${darkMode ? 'bg-slate-600 hover:bg-slate-500' : 'bg-gray-300 hover:bg-gray-400'}`}
                      >
                        Modifica
                      </button>
                      <button
                        onClick={() => deleteImpianto(i.id)}
                        className="px-3 py-2 rounded-lg text-sm bg-red-600 hover:bg-red-700 text-white"
                      >
                        Elimina
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {activeTab === 'attendance' && (
          <div>
            <div className="flex flex-col sm:flex-row gap-2 mb-4">
              <label
                className={`flex-1 cursor-pointer px-4 py-3 rounded-lg flex items-center gap-2 justify-center font-medium ${
                  darkMode ? 'bg-slate-700 hover:bg-slate-600' : 'bg-gray-200 hover:bg-gray-300'
                } ${attendanceImporting ? 'opacity-60 cursor-not-allowed' : ''}`}
              >
                <Upload size={20} />
                {attendanceImporting ? 'Importazione...' : 'Importa da Excel'}
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  disabled={attendanceImporting}
                  onChange={(e) => {
                    const file = e.target.files && e.target.files[0];
                    importAttendanceFile(file);
                    e.target.value = '';
                  }}
                  className="hidden"
                />
              </label>
              <button
                onClick={downloadAttendanceExport}
                disabled={attendanceExporting}
                className={`flex-1 px-4 py-3 rounded-lg flex items-center gap-2 justify-center font-medium text-white ${
                  darkMode ? 'bg-emerald-700 hover:bg-emerald-600' : 'bg-emerald-600 hover:bg-emerald-700'
                } ${attendanceExporting ? 'opacity-60 cursor-not-allowed' : ''}`}
              >
                <Download size={20} />
                {attendanceExporting ? 'Download...' : 'Esporta Excel'}
              </button>
            </div>

            {attendanceImportResult && (
              <div className={`mb-4 p-4 rounded-lg ${darkMode ? 'bg-slate-700' : 'bg-gray-100'}`}>
                <p className="font-semibold mb-1">Risultato importazione</p>
                <p className="text-sm">Importate: {attendanceImportResult.imported} — Saltate: {attendanceImportResult.skipped}</p>
                {attendanceImportResult.errors && attendanceImportResult.errors.length > 0 && (
                  <div className="mt-2 space-y-1 max-h-64 overflow-y-auto">
                    {attendanceImportResult.errors.map((e, idx) => (
                      <p key={idx} className={`text-xs ${darkMode ? 'text-amber-300' : 'text-amber-700'}`}>
                        Riga {e.row}: {e.reason}
                      </p>
                    ))}
                  </div>
                )}
                <button
                  onClick={() => setAttendanceImportResult(null)}
                  className={`mt-3 px-3 py-1.5 rounded-lg text-xs ${darkMode ? 'bg-slate-600 hover:bg-slate-500' : 'bg-gray-300 hover:bg-gray-400'}`}
                >
                  Chiudi
                </button>
              </div>
            )}

            <p className={`text-xs ${darkMode ? 'text-slate-500' : 'text-gray-400'}`}>
              Esporta o importa lo storico presenze in formato Excel (colonne Nome, Cognome, Codice tessera FIN, ID FIN, Turno, Data, Presente, Impianto) — comodo per travasare i dati di appello da/verso altri portali simili. Un file esportato da qui è re-importabile invariato. In import, ogni riga viene abbinata a un atleta esistente per ID FIN, poi codice tessera, poi nome e cognome (omonimi senza codici FIN vengono scartati, mai indovinati), a un turno e a un impianto esistenti per nome (Impianto vuoto = impianto dell'atleta): righe senza corrispondenza vengono segnalate e saltate, senza alterare il resto dei dati.
            </p>
          </div>
        )}

        {activeTab === 'races' && (
          <RacesTab athletes={athletes} darkMode={darkMode} />
        )}

        {activeTab === 'backup' && (
          <div>
            <div className="flex flex-col sm:flex-row gap-2 mb-4">
              <button
                onClick={downloadDbBackup}
                disabled={dbBackupDownloading}
                className={`flex-1 px-4 py-3 rounded-lg flex items-center gap-2 justify-center font-medium text-white ${
                  darkMode ? 'bg-emerald-700 hover:bg-emerald-600' : 'bg-emerald-600 hover:bg-emerald-700'
                } ${dbBackupDownloading ? 'opacity-60 cursor-not-allowed' : ''}`}
              >
                <Download size={20} />
                {dbBackupDownloading ? 'Download...' : 'Scarica backup (.sql)'}
              </button>
              <label
                className={`flex-1 cursor-pointer px-4 py-3 rounded-lg flex items-center gap-2 justify-center font-medium ${
                  darkMode ? 'bg-red-900/60 hover:bg-red-900/80 text-red-100' : 'bg-red-100 hover:bg-red-200 text-red-800'
                } ${dbRestoreUploading ? 'opacity-60 cursor-not-allowed' : ''}`}
              >
                <Upload size={20} />
                {dbRestoreUploading ? 'Ripristino...' : 'Ripristina da backup'}
                <input
                  type="file"
                  accept=".sql"
                  disabled={dbRestoreUploading}
                  onChange={(e) => {
                    const file = e.target.files && e.target.files[0];
                    restoreDbBackup(file);
                    e.target.value = '';
                  }}
                  className="hidden"
                />
              </label>
            </div>

            {prodPull.enabled && (
              <div className={`mb-4 p-4 rounded-lg border ${darkMode ? 'border-amber-700 bg-amber-900/20' : 'border-amber-300 bg-amber-50'}`}>
                <p className="font-semibold mb-1">Importa dati da produzione <span className="text-xs font-normal opacity-70">(solo ambiente di sviluppo)</span></p>
                <p className={`text-xs mb-3 ${darkMode ? 'text-slate-400' : 'text-gray-600'}`}>
                  Sostituisce questo database di sviluppo con una copia di quello di produzione ({prodPull.prodUrl}), per provare la nuova versione sui dati reali. La produzione viene solo letta. Serve il token di un admin di produzione: accedi al pannello admin di produzione, apri la console del browser (F12) ed esegui <code>localStorage.getItem('token')</code>. Il token non viene salvato.
                </p>
                <input
                  type="password"
                  autoComplete="off"
                  placeholder="Token admin di produzione"
                  value={prodToken}
                  onChange={(e) => setProdToken(e.target.value)}
                  disabled={prodPulling}
                  className={`w-full mb-2 px-4 py-3 rounded-lg border ${darkMode ? 'bg-slate-700 border-slate-600' : 'bg-white border-gray-300'}`}
                />
                <label className="flex items-center gap-2 text-sm mb-3">
                  <input
                    type="checkbox"
                    checked={prodPullBackupFirst}
                    onChange={(e) => setProdPullBackupFirst(e.target.checked)}
                    disabled={prodPulling}
                  />
                  Scarica prima un backup del database di sviluppo (per poter tornare indietro)
                </label>
                <button
                  onClick={pullFromProduction}
                  disabled={prodPulling || !prodToken.trim()}
                  className="w-full px-4 py-3 rounded-lg flex items-center gap-2 justify-center font-medium text-white bg-amber-600 hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Download size={20} />
                  {prodPulling ? 'Importazione da produzione...' : 'Importa dati da produzione'}
                </button>
              </div>
            )}

            {dbRestoreResult && (
              <div className={`mb-4 p-4 rounded-lg ${dbRestoreResult.success
                ? (darkMode ? 'bg-emerald-900/40 text-emerald-200' : 'bg-emerald-50 text-emerald-800')
                : (darkMode ? 'bg-red-900/40 text-red-200' : 'bg-red-50 text-red-800')}`}
              >
                <p className="font-semibold mb-1">{dbRestoreResult.success ? 'Ripristino completato' : 'Ripristino fallito'}</p>
                <p className="text-sm">{dbRestoreResult.message}</p>
                {dbRestoreResult.success && dbRestoreResult.schemaUpdated && (
                  <p className="text-sm mt-1">
                    Lo schema del database è stato aggiornato automaticamente alla versione corrente: gli eventuali campi introdotti dopo la creazione del backup (es. taglia maglietta) sono stati aggiunti vuoti, un backup precedente agli impianti viene assegnato a Pordenone e i contatori delle chiavi sono stati riallineati. I dati mostrati nell'app sono stati ricaricati.
                  </p>
                )}
                {dbRestoreResult.success && dbRestoreResult.counts && (
                  <p className="text-xs mt-2 opacity-80">
                    Righe ripristinate: {Object.entries(dbRestoreResult.counts).map(([t, n]) => `${t} ${n}`).join(' · ')}
                  </p>
                )}
                {dbRestoreResult.oldSchema && (
                  <p className="text-sm mt-1">
                    La produzione ha ancora lo schema precedente agli impianti: qui è stato aggiornato e utenti, atleti e presenze sono stati assegnati a Pordenone — è quello che succederà in produzione al prossimo deploy.
                  </p>
                )}
                {dbRestoreResult.fromProduction && (
                  <p className="text-sm mt-1 font-medium">
                    Utenti e whitelist ora sono quelli di produzione: esci e rientra con Google. Il database di sviluppo contiene dati personali reali.
                  </p>
                )}
                {dbRestoreResult.detail && (
                  <pre className="mt-2 text-xs whitespace-pre-wrap max-h-64 overflow-y-auto">{dbRestoreResult.detail}</pre>
                )}
                <button
                  onClick={() => setDbRestoreResult(null)}
                  className={`mt-3 px-3 py-1.5 rounded-lg text-xs ${darkMode ? 'bg-slate-600 hover:bg-slate-500' : 'bg-gray-300 hover:bg-gray-400'}`}
                >
                  Chiudi
                </button>
              </div>
            )}

            <p className={`text-xs ${darkMode ? 'text-slate-500' : 'text-gray-400'}`}>
              "Scarica backup" genera un dump completo del database (schema + tutti i dati: atleti con tutti i loro campi, taglia maglietta e identificativi FIN compresi, presenze, manifestazioni con le relative gare e tutti i tempi registrati, tipologie di gara, utenti, categorie, turni, whitelist Google) in un file .sql, comodo per popolare velocemente un ambiente nuovo invece di reinserire tutto a mano. "Ripristina da backup" carica un file .sql generato da qui e <strong>sostituisce integralmente</strong> il database corrente con il suo contenuto — un'operazione distruttiva e non reversibile, pensata per ambienti nuovi o per un rollback completo, non per un uso quotidiano. Si può ripristinare anche un backup creato con una versione precedente dell'app: subito dopo il ripristino lo schema viene aggiornato automaticamente alla versione corrente e i campi introdotti dopo (es. taglia) vengono aggiunti vuoti.
            </p>
          </div>
        )}
      </div>

      {/* Logout Button */}
      <div className="fixed bottom-6 left-4 right-4 max-w-4xl mx-auto">
        <button
          onClick={logout}
          className="w-full bg-red-600 hover:bg-red-700 text-white px-4 py-3 rounded-lg flex items-center gap-2 justify-center"
        >
          <LogOut size={20} />
          Logout
        </button>
      </div>

      {editingUser !== null && (
        <UserFormModal
          userAccount={editingUser}
          categories={categories}
          courseDays={courseDays}
          impianti={impianti}
          onSave={saveUser}
          onCancel={() => setEditingUser(null)}
          darkMode={darkMode}
        />
      )}

      {editingAthlete !== null && (
        <AthleteFormModal
          athlete={editingAthlete}
          categories={categories}
          impianti={impianti}
          onSave={saveAthlete}
          onCancel={() => setEditingAthlete(null)}
          darkMode={darkMode}
        />
      )}

      {finImportAthlete !== null && (
        <FinImportModal
          athlete={finImportAthlete}
          onClose={(imported) => { setFinImportAthlete(null); if (imported) fetchAthletes(); }}
          darkMode={darkMode}
        />
      )}

      {editingWhitelistEntry !== null && (
        <WhitelistFormModal
          entry={editingWhitelistEntry}
          onSave={saveWhitelistEntry}
          onCancel={() => setEditingWhitelistEntry(null)}
          darkMode={darkMode}
        />
      )}

      {editingCategory !== null && (
        <CategoryFormModal
          category={editingCategory}
          courseDays={courseDays}
          onSave={saveCategory}
          onCancel={() => setEditingCategory(null)}
          darkMode={darkMode}
        />
      )}

      {editingCourseDay !== null && (
        <CourseDayFormModal
          courseDay={editingCourseDay}
          onSave={saveCourseDay}
          onCancel={() => setEditingCourseDay(null)}
          darkMode={darkMode}
        />
      )}

      {editingImpianto !== null && (
        <ImpiantoFormModal
          impianto={editingImpianto}
          onSave={saveImpianto}
          onCancel={() => setEditingImpianto(null)}
          darkMode={darkMode}
        />
      )}
    </div>
  );
}

function AccessDeniedScreen({ user, onLogout, darkMode }) {
  return (
    <div className={`min-h-screen flex items-center justify-center ${darkMode ? 'bg-slate-900 text-white' : 'bg-gray-50 text-slate-900'} px-4`}>
      <div className={`w-full max-w-md p-8 rounded-xl shadow-lg text-center ${darkMode ? 'bg-slate-800' : 'bg-white'}`}>
        <ShieldAlert size={40} className="mx-auto mb-4 text-red-500" />
        <h2 className="text-xl font-bold mb-2">Accesso riservato agli amministratori</h2>
        <p className={`text-sm mb-6 ${darkMode ? 'text-slate-400' : 'text-gray-600'}`}>
          L'account <strong>{user.name}</strong> non ha il ruolo di amministratore: questo pannello è visibile solo agli account admin. Usa l'app principale per l'appello e l'anagrafica atleti.
        </p>
        <button
          onClick={onLogout}
          className="w-full bg-red-600 hover:bg-red-700 text-white px-4 py-3 rounded-lg flex items-center gap-2 justify-center"
        >
          <LogOut size={20} />
          Logout
        </button>
      </div>
    </div>
  );
}

function LoginScreen({ onGoogleLogin, googleClientId, darkMode, setDarkMode }) {
  const [backendStatus, setBackendStatus] = useState('checking');
  const googleButtonRef = useRef(null);

  useEffect(() => {
    if (!googleClientId) return;

    let cancelled = false;
    const tryRender = () => {
      if (cancelled || !googleButtonRef.current) return;
      if (!window.google?.accounts?.id) {
        setTimeout(tryRender, 200);
        return;
      }

      window.google.accounts.id.initialize({
        client_id: googleClientId,
        callback: (response) => onGoogleLogin(response.credential)
      });

      window.google.accounts.id.renderButton(googleButtonRef.current, {
        theme: darkMode ? 'filled_black' : 'outline',
        size: 'large',
        width: 320,
        locale: 'it'
      });
    };

    tryRender();
    return () => { cancelled = true; };
  }, [googleClientId, darkMode, onGoogleLogin]);

  useEffect(() => {
    let cancelled = false;

    const checkHealth = async () => {
      try {
        const response = await fetch(`${API_URL}/health`);
        let body = null;
        try {
          body = await response.json();
        } catch (parseErr) {
          body = null;
        }
        if (cancelled) return;
        if (response.ok && body?.database === 'connected') {
          setBackendStatus('ok');
        } else {
          setBackendStatus('db-down');
        }
      } catch (err) {
        if (!cancelled) setBackendStatus('unreachable');
      }
    };

    checkHealth();
    const interval = setInterval(checkHealth, 15000);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, []);

  const statusConfig = {
    checking: {
      icon: <Clock size={16} className={darkMode ? 'text-slate-400' : 'text-gray-500'} />,
      text: 'Verifica connessione al server…',
      className: darkMode ? 'text-slate-400' : 'text-gray-500',
    },
    ok: {
      icon: <CheckCircle size={16} className="text-green-500" />,
      text: 'Server connesso',
      className: darkMode ? 'text-green-400' : 'text-green-600',
    },
    unreachable: {
      icon: <WifiOff size={16} className="text-red-500" />,
      text: 'Impossibile raggiungere il server',
      className: darkMode ? 'text-red-400' : 'text-red-600',
    },
    'db-down': {
      icon: <AlertCircle size={16} className="text-red-500" />,
      text: 'Server raggiungibile ma database non connesso',
      className: darkMode ? 'text-red-400' : 'text-red-600',
    },
  };

  const status = statusConfig[backendStatus];
  const loginDisabled = backendStatus === 'unreachable';

  return (
    <div className={`min-h-screen flex items-center justify-center ${darkMode ? 'bg-slate-900' : 'bg-gray-50'} px-4`}>
      <div className={`w-full max-w-md p-8 rounded-xl shadow-lg ${darkMode ? 'bg-slate-800' : 'bg-white'}`}>
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold mb-2 flex items-center justify-center gap-2">
            <Shield size={32} />
          </h1>
          <h2 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-slate-900'}`}>Admin — Appello Nuoto</h2>
          <p className={`text-sm mt-2 ${darkMode ? 'text-slate-400' : 'text-gray-600'}`}>Pannello di amministrazione, solo per account admin</p>
          <div className={`flex items-center justify-center gap-2 mt-3 text-xs ${status.className}`}>
            {status.icon}
            <span>{status.text}</span>
          </div>
        </div>

        {(backendStatus === 'unreachable' || backendStatus === 'db-down') && (
          <div className={`mb-4 px-4 py-3 rounded-lg text-sm flex items-start gap-2 ${darkMode ? 'bg-red-900/30 text-red-300' : 'bg-red-50 text-red-700'}`}>
            <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
            <span>
              {backendStatus === 'unreachable'
                ? 'Il server non risponde. Controlla la connessione o riprova più tardi.'
                : 'Il server è raggiungibile ma non riesce a connettersi al database. Riprova più tardi.'}
            </span>
          </div>
        )}

        {googleClientId ? (
          <div className={`flex justify-center ${loginDisabled ? 'opacity-50 pointer-events-none' : ''}`}>
            <div ref={googleButtonRef} />
          </div>
        ) : (
          <p className={`text-center text-sm ${darkMode ? 'text-slate-400' : 'text-gray-600'}`}>
            Login con Google non configurato su questo ambiente. Contatta l'amministratore.
          </p>
        )}

        <div className="mt-6 text-center">
          <button
            onClick={() => setDarkMode(!darkMode)}
            className={`px-4 py-2 rounded-lg ${darkMode ? 'bg-slate-700' : 'bg-gray-200'}`}
          >
            {darkMode ? 'Light' : 'Dark'} Mode
          </button>
        </div>

        <p className={`mt-4 text-center text-xs ${darkMode ? 'text-slate-500' : 'text-gray-400'}`}>
          {APP_VERSION_LABEL}
        </p>
      </div>
    </div>
  );
}

function AthleteFormModal({ athlete, categories, impianti = [], defaultImpianto = '', onSave, onCancel, darkMode }) {
  const isEditing = Boolean(athlete.id);
  const [nome, setNome] = useState(athlete.nome || '');
  const [cognome, setCognome] = useState(athlete.cognome || '');
  const [dataNascita, setDataNascita] = useState(
    athlete.data_nascita ? athlete.data_nascita.split('T')[0] : ''
  );
  const [email, setEmail] = useState(athlete.email || '');
  const [telefono, setTelefono] = useState(athlete.telefono || '');
  const [categoria, setCategoria] = useState(athlete.categoria || categories[0]?.nome || '');
  const [taglia, setTaglia] = useState(athlete.taglia || '');
  // Identificativi FIN facoltativi (univoci lato backend se presenti): servono ad abbinare
  // l'atleta nell'import dei tempi gara (ID FIN → codice tessera → nome+cognome).
  const [codiceTesseraFin, setCodiceTesseraFin] = useState(athlete.codice_tessera_fin || '');
  const [idFin, setIdFin] = useState(athlete.id_fin || '');
  // Di norma un solo impianto; un nuovo atleta parte da Pordenone (o dal primo impianto)
  const [athleteImpianti, setAthleteImpianti] = useState(
    athlete.impianti && athlete.impianti.length > 0
      ? athlete.impianti
      : [defaultImpianto || impianti.find(i => i.nome === 'Pordenone')?.nome || impianti[0]?.nome].filter(Boolean)
  );

  const toggleImpianto = (nome) => {
    setAthleteImpianti(prev => prev.includes(nome) ? prev.filter(n => n !== nome) : [...prev, nome]);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!nome.trim() || !cognome.trim()) return;
    if (impianti.length > 0 && athleteImpianti.length === 0) {
      alert('Seleziona almeno un impianto');
      return;
    }

    onSave({
      ...(isEditing ? { id: athlete.id } : {}),
      nome: nome.trim(),
      cognome: cognome.trim(),
      data_nascita: dataNascita || null,
      email: email.trim() || null,
      telefono: telefono.trim() || null,
      categoria,
      taglia: taglia || null,
      codice_tessera_fin: codiceTesseraFin.trim() || null,
      id_fin: idFin.trim() || null,
      impianti: athleteImpianti
    });
  };

  const inputClass = `w-full px-4 py-3 rounded-lg border ${darkMode ? 'bg-slate-700 border-slate-600' : 'bg-gray-100 border-gray-300'} focus:outline-none focus:ring-2 focus:ring-blue-500`;

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center px-4 z-50">
      <div className={`w-full max-w-md p-6 rounded-xl shadow-lg ${darkMode ? 'bg-slate-800' : 'bg-white'} max-h-[90vh] overflow-y-auto`}>
        <h3 className="text-xl font-bold mb-4">
          {isEditing ? 'Modifica Atleta' : 'Aggiungi Atleta'}
        </h3>

        <form onSubmit={handleSubmit} className="space-y-4">
          <input
            type="text"
            placeholder="Nome"
            value={nome}
            onChange={(e) => setNome(e.target.value)}
            required
            className={inputClass}
          />
          <input
            type="text"
            placeholder="Cognome"
            value={cognome}
            onChange={(e) => setCognome(e.target.value)}
            required
            className={inputClass}
          />
          <div>
            <label className="block text-xs opacity-60 mb-1">Data di nascita</label>
            <input
              type="date"
              value={dataNascita}
              onChange={(e) => setDataNascita(e.target.value)}
              className={inputClass}
            />
          </div>
          <input
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={inputClass}
          />
          <input
            type="tel"
            placeholder="Numero di telefono"
            value={telefono}
            onChange={(e) => setTelefono(e.target.value)}
            className={inputClass}
          />
          <div>
            <label className="block text-xs opacity-60 mb-1">Categoria</label>
            <select
              value={categoria}
              onChange={(e) => setCategoria(e.target.value)}
              className={inputClass}
            >
              {categories.map(c => (
                <option key={c.id} value={c.nome}>{c.nome}</option>
              ))}
            </select>
          </div>
          {impianti.length > 0 && (
            <div>
              <label className="block text-xs opacity-60 mb-1">Impianti (di norma uno solo)</label>
              <div className="flex flex-wrap gap-2">
                {impianti.map(i => (
                  <label key={i.id} className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm cursor-pointer ${darkMode ? 'bg-slate-700' : 'bg-gray-100'}`}>
                    <input
                      type="checkbox"
                      checked={athleteImpianti.includes(i.nome)}
                      onChange={() => toggleImpianto(i.nome)}
                    />
                    {i.nome}
                  </label>
                ))}
              </div>
            </div>
          )}
          <div>
            <label className="block text-xs opacity-60 mb-1">Taglia maglietta</label>
            <select
              value={taglia}
              onChange={(e) => setTaglia(e.target.value)}
              className={inputClass}
            >
              <option value="">— Non indicata —</option>
              {TAGLIE.map(t => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs opacity-60 mb-1">Codice tessera FIN</label>
            <input
              type="text"
              placeholder="Facoltativo"
              value={codiceTesseraFin}
              onChange={(e) => setCodiceTesseraFin(e.target.value)}
              className={inputClass}
            />
          </div>
          <div>
            <label className="block text-xs opacity-60 mb-1">ID FIN</label>
            <input
              type="text"
              placeholder="Facoltativo"
              value={idFin}
              onChange={(e) => setIdFin(e.target.value)}
              className={inputClass}
            />
          </div>

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onCancel}
              className={`flex-1 px-4 py-3 rounded-lg font-semibold ${darkMode ? 'bg-slate-700 hover:bg-slate-600' : 'bg-gray-200 hover:bg-gray-300'}`}
            >
              Annulla
            </button>
            <button
              type="submit"
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 rounded-lg transition"
            >
              Salva
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function UserFormModal({ userAccount, categories, courseDays, impianti = [], onSave, onCancel, darkMode }) {
  const isEditing = Boolean(userAccount.id);
  const [username, setUsername] = useState(userAccount.username || '');
  const [email, setEmail] = useState(userAccount.email || '');
  const [role, setRole] = useState(userAccount.role || 'instructor');
  const [active, setActive] = useState(userAccount.active !== undefined ? userAccount.active : true);
  const [categorie, setCategorie] = useState(userAccount.categorie || []);
  const [turni, setTurni] = useState(userAccount.turni || []);
  const [userImpianti, setUserImpianti] = useState(userAccount.impianti || []);

  const toggleImpianto = (value) => {
    setUserImpianti(prev => prev.includes(value) ? prev.filter(i => i !== value) : [...prev, value]);
  };

  const toggleCategoria = (value) => {
    setCategorie(prev => prev.includes(value) ? prev.filter(c => c !== value) : [...prev, value]);
  };

  const toggleTurno = (value) => {
    setTurni(prev => prev.includes(value) ? prev.filter(t => t !== value) : [...prev, value]);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!isEditing && (!username.trim() || !email.trim())) return;

    const payload = {
      ...(isEditing ? { id: userAccount.id } : { username: username.trim() }),
      email: email.trim() || null,
      role,
      active,
      categorie,
      turni,
      impianti: userImpianti
    };

    onSave(payload);
  };

  const inputClass = `w-full px-4 py-3 rounded-lg border ${darkMode ? 'bg-slate-700 border-slate-600' : 'bg-gray-100 border-gray-300'} focus:outline-none focus:ring-2 focus:ring-blue-500`;

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center px-4 z-50">
      <div className={`w-full max-w-md p-6 rounded-xl shadow-lg ${darkMode ? 'bg-slate-800' : 'bg-white'} max-h-[90vh] overflow-y-auto`}>
        <h3 className="text-xl font-bold mb-4">
          {isEditing ? 'Modifica Account' : 'Aggiungi Account'}
        </h3>

        <form onSubmit={handleSubmit} className="space-y-4">
          <input
            type="text"
            placeholder="Username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            required
            disabled={isEditing}
            className={`${inputClass} ${isEditing ? 'opacity-60 cursor-not-allowed' : ''}`}
          />
          <input
            type="email"
            placeholder="Email (Google, obbligatoria: è l'unico modo per accedere)"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className={inputClass}
          />
          <div>
            <label className="block text-xs opacity-60 mb-1">Ruolo</label>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value)}
              className={inputClass}
            >
              <option value="instructor">Istruttore</option>
              <option value="admin">Amministratore</option>
            </select>
          </div>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={active}
              onChange={(e) => setActive(e.target.checked)}
              className="w-5 h-5"
            />
            <span>Account attivo</span>
          </label>

          {role === 'instructor' && (
            <>
              <div>
                <label className="block text-xs opacity-60 mb-1">Categorie gestite</label>
                <div className="flex flex-wrap gap-2">
                  {categories.map(c => (
                    <label
                      key={c.id}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm cursor-pointer select-none ${
                        categorie.includes(c.nome)
                          ? 'bg-blue-600 text-white'
                          : darkMode ? 'bg-slate-700' : 'bg-gray-200'
                      }`}
                    >
                      <input
                        type="checkbox"
                        className="hidden"
                        checked={categorie.includes(c.nome)}
                        onChange={() => toggleCategoria(c.nome)}
                      />
                      {c.nome}
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-xs opacity-60 mb-1">Turni settimanali</label>
                <div className="flex flex-wrap gap-2">
                  {courseDays.map(d => (
                    <label
                      key={d.id}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm cursor-pointer select-none ${
                        turni.includes(d.nome)
                          ? 'bg-blue-600 text-white'
                          : darkMode ? 'bg-slate-700' : 'bg-gray-200'
                      }`}
                    >
                      <input
                        type="checkbox"
                        className="hidden"
                        checked={turni.includes(d.nome)}
                        onChange={() => toggleTurno(d.nome)}
                      />
                      {d.nome.charAt(0).toUpperCase() + d.nome.slice(1)}
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-xs opacity-60 mb-1">Impianti (nessuno = tutti)</label>
                <div className="flex flex-wrap gap-2">
                  {impianti.map(i => (
                    <label
                      key={i.id}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm cursor-pointer select-none ${
                        userImpianti.includes(i.nome)
                          ? 'bg-blue-600 text-white'
                          : darkMode ? 'bg-slate-700' : 'bg-gray-200'
                      }`}
                    >
                      <input
                        type="checkbox"
                        className="hidden"
                        checked={userImpianti.includes(i.nome)}
                        onChange={() => toggleImpianto(i.nome)}
                      />
                      {i.nome}
                    </label>
                  ))}
                </div>
              </div>
            </>
          )}

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onCancel}
              className={`flex-1 px-4 py-3 rounded-lg font-semibold ${darkMode ? 'bg-slate-700 hover:bg-slate-600' : 'bg-gray-200 hover:bg-gray-300'}`}
            >
              Annulla
            </button>
            <button
              type="submit"
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 rounded-lg transition"
            >
              Salva
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function WhitelistFormModal({ entry, onSave, onCancel, darkMode }) {
  const isEditing = Boolean(entry.id);
  const [email, setEmail] = useState(entry.email || '');
  const [role, setRole] = useState(entry.role || 'instructor');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!email.trim()) return;

    onSave({
      ...(isEditing ? { id: entry.id } : {}),
      email: email.trim(),
      role
    });
  };

  const inputClass = `w-full px-4 py-3 rounded-lg border ${darkMode ? 'bg-slate-700 border-slate-600' : 'bg-gray-100 border-gray-300'} focus:outline-none focus:ring-2 focus:ring-blue-500`;

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center px-4 z-50">
      <div className={`w-full max-w-md p-6 rounded-xl shadow-lg ${darkMode ? 'bg-slate-800' : 'bg-white'}`}>
        <h3 className="text-xl font-bold mb-4">
          {isEditing ? 'Modifica Email Whitelist' : 'Aggiungi Email alla Whitelist'}
        </h3>

        <form onSubmit={handleSubmit} className="space-y-4">
          <input
            type="email"
            placeholder="Email Google"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className={inputClass}
          />
          <div>
            <label className="block text-xs opacity-60 mb-1">Ruolo assegnato alla registrazione</label>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value)}
              className={inputClass}
            >
              <option value="instructor">Istruttore</option>
              <option value="admin">Amministratore</option>
            </select>
          </div>

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onCancel}
              className={`flex-1 px-4 py-3 rounded-lg font-semibold ${darkMode ? 'bg-slate-700 hover:bg-slate-600' : 'bg-gray-200 hover:bg-gray-300'}`}
            >
              Annulla
            </button>
            <button
              type="submit"
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 rounded-lg transition"
            >
              Salva
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function CategoryFormModal({ category, courseDays, onSave, onCancel, darkMode }) {
  const isEditing = Boolean(category.id);
  const [nome, setNome] = useState(category.nome || '');
  const [giorni, setGiorni] = useState(category.giorni || []);

  const toggleGiorno = (value) => {
    setGiorni(prev => prev.includes(value) ? prev.filter(g => g !== value) : [...prev, value]);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!nome.trim()) return;

    onSave({
      ...(isEditing ? { id: category.id } : {}),
      nome: nome.trim(),
      giorni
    });
  };

  const inputClass = `w-full px-4 py-3 rounded-lg border ${darkMode ? 'bg-slate-700 border-slate-600' : 'bg-gray-100 border-gray-300'} focus:outline-none focus:ring-2 focus:ring-blue-500`;

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center px-4 z-50">
      <div className={`w-full max-w-md p-6 rounded-xl shadow-lg ${darkMode ? 'bg-slate-800' : 'bg-white'} max-h-[90vh] overflow-y-auto`}>
        <h3 className="text-xl font-bold mb-4">
          {isEditing ? 'Modifica Categoria' : 'Aggiungi Categoria'}
        </h3>

        <form onSubmit={handleSubmit} className="space-y-4">
          <input
            type="text"
            placeholder="Nome categoria"
            value={nome}
            onChange={(e) => setNome(e.target.value)}
            required
            className={inputClass}
          />

          <div>
            <label className="block text-xs opacity-60 mb-1">
              Turni settimanali (usati per calcolare il totale sessioni nelle statistiche mensili)
            </label>
            <div className="flex flex-wrap gap-2">
              {courseDays.map(d => (
                <label
                  key={d.id}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm cursor-pointer select-none ${
                    giorni.includes(d.nome)
                      ? 'bg-blue-600 text-white'
                      : darkMode ? 'bg-slate-700' : 'bg-gray-200'
                  }`}
                >
                  <input
                    type="checkbox"
                    className="hidden"
                    checked={giorni.includes(d.nome)}
                    onChange={() => toggleGiorno(d.nome)}
                  />
                  {d.nome.charAt(0).toUpperCase() + d.nome.slice(1)}
                </label>
              ))}
            </div>
          </div>

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onCancel}
              className={`flex-1 px-4 py-3 rounded-lg font-semibold ${darkMode ? 'bg-slate-700 hover:bg-slate-600' : 'bg-gray-200 hover:bg-gray-300'}`}
            >
              Annulla
            </button>
            <button
              type="submit"
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 rounded-lg transition"
            >
              Salva
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function CourseDayFormModal({ courseDay, onSave, onCancel, darkMode }) {
  const isEditing = Boolean(courseDay.id);
  const [nome, setNome] = useState(courseDay.nome || '');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!nome.trim()) return;

    onSave({
      ...(isEditing ? { id: courseDay.id } : {}),
      nome: nome.trim()
    });
  };

  const inputClass = `w-full px-4 py-3 rounded-lg border ${darkMode ? 'bg-slate-700 border-slate-600' : 'bg-gray-100 border-gray-300'} focus:outline-none focus:ring-2 focus:ring-blue-500`;

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center px-4 z-50">
      <div className={`w-full max-w-md p-6 rounded-xl shadow-lg ${darkMode ? 'bg-slate-800' : 'bg-white'}`}>
        <h3 className="text-xl font-bold mb-4">
          {isEditing ? 'Modifica Turno' : 'Aggiungi Turno'}
        </h3>

        <form onSubmit={handleSubmit} className="space-y-4">
          <input
            type="text"
            placeholder="Nome giorno (es. lunedì)"
            value={nome}
            onChange={(e) => setNome(e.target.value)}
            required
            className={inputClass}
          />

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onCancel}
              className={`flex-1 px-4 py-3 rounded-lg font-semibold ${darkMode ? 'bg-slate-700 hover:bg-slate-600' : 'bg-gray-200 hover:bg-gray-300'}`}
            >
              Annulla
            </button>
            <button
              type="submit"
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 rounded-lg transition"
            >
              Salva
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ImpiantoFormModal({ impianto, onSave, onCancel, darkMode }) {
  const isEditing = Boolean(impianto.id);
  const [nome, setNome] = useState(impianto.nome || '');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!nome.trim()) return;

    onSave({
      ...(isEditing ? { id: impianto.id } : {}),
      nome: nome.trim()
    });
  };

  const inputClass = `w-full px-4 py-3 rounded-lg border ${darkMode ? 'bg-slate-700 border-slate-600' : 'bg-gray-100 border-gray-300'} focus:outline-none focus:ring-2 focus:ring-blue-500`;

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center px-4 z-50">
      <div className={`w-full max-w-md p-6 rounded-xl shadow-lg ${darkMode ? 'bg-slate-800' : 'bg-white'}`}>
        <h3 className="text-xl font-bold mb-4">
          {isEditing ? 'Modifica Impianto' : 'Aggiungi Impianto'}
        </h3>

        <form onSubmit={handleSubmit} className="space-y-4">
          <input
            type="text"
            placeholder="Nome impianto (es. Pordenone)"
            value={nome}
            onChange={(e) => setNome(e.target.value)}
            required
            className={inputClass}
          />

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onCancel}
              className={`flex-1 px-4 py-3 rounded-lg font-semibold ${darkMode ? 'bg-slate-700 hover:bg-slate-600' : 'bg-gray-200 hover:bg-gray-300'}`}
            >
              Annulla
            </button>
            <button
              type="submit"
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 rounded-lg transition"
            >
              Salva
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ============ GARE / MANIFESTAZIONI ============

// Suggerimenti per il campo "tipo" della manifestazione (testo libero, vedi contratto API).
const TIPI_MANIFESTAZIONE = ['Regionale', 'Interregionale', 'Campionati Regionali', 'Campionati Italiani', 'Trofeo', 'Meeting', 'Altro'];

// Ordine di visualizzazione degli stili delle gare individuali.
const STILI_GARA = ['stile libero', 'dorso', 'rana', 'farfalla', 'misti'];

// Stati di un risultato alternativi al tempo: SQ = squalificato, RT = ritirato, NP = non partito.
const STATI_GARA = [
  { value: 'SQ', label: 'SQ (squalificato)' },
  { value: 'RT', label: 'RT (ritirato)' },
  { value: 'NP', label: 'NP (non partito)' }
];

// Formato tempo "MM:SS,cc" — duplicato da parseTempo/formatTempo in backend/src/server.js.
function formatTempo(cs) {
  if (cs === null || cs === undefined || cs === '') return '';
  const n = Number(cs);
  if (!Number.isFinite(n)) return '';
  const minutes = Math.floor(n / 6000);
  const seconds = Math.floor((n % 6000) / 100);
  const cents = n % 100;
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')},${String(cents).padStart(2, '0')}`;
}

// Validazione indicativa lato client — duplicato da parseTempo/formatTempo in backend/src/server.js.
// Accetta "01:05,32", "1:05.32", "65,32", "1'05\"32". La validazione vera resta lato server.
function isValidTempo(str) {
  const s = String(str || '').trim().replace(/[’']/g, ':').replace(/[”"]/g, ',').replace(/\s+/g, '');
  const m = /^(?:(\d{1,3}):)?(\d{1,2})(?:[.,](\d{1,2}))?$/.exec(s);
  if (!m) return false;
  if (m[1] && Number(m[2]) >= 60) return false;
  return true;
}

// Data "YYYY-MM-DD" in formato it-IT, senza passare da UTC (eviterebbe lo slittamento di un giorno).
function formatDataGara(data) {
  if (!data) return '';
  const [y, m, d] = String(data).slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return data;
  return new Date(y, m - 1, d).toLocaleDateString('it-IT');
}

// fetch autenticata verso il backend: restituisce il JSON e lancia un Error col messaggio
// `error` del backend (in italiano) se la risposta non è ok.
async function racesApi(path, options = {}) {
  const token = localStorage.getItem('token');
  const headers = { 'Authorization': `Bearer ${token}` };
  if (options.body && !(options.body instanceof FormData)) headers['Content-Type'] = 'application/json';
  const response = await fetch(`${API_URL}${path}`, { ...options, headers });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.error || `Errore ${response.status}: ${response.statusText}`);
  return data;
}

// Riquadro errore riutilizzato nelle sezioni della tab Gare.
function ErrorBox({ message, onClose, darkMode }) {
  if (!message) return null;
  return (
    <div className={`mb-3 p-3 rounded-lg text-sm flex items-start gap-2 ${darkMode ? 'bg-red-900/40 text-red-200' : 'bg-red-50 text-red-800'}`}>
      <AlertCircle size={16} className="shrink-0 mt-0.5" />
      <span className="flex-1">{message}</span>
      {onClose && (
        <button type="button" onClick={onClose} className="text-xs underline shrink-0">Chiudi</button>
      )}
    </div>
  );
}

// Colori e testi degli esiti dell'import da FIN Veneto (POST /api/athletes/:id/fin-import).
const ESITI_FIN = {
  nuova: { label: 'Nuova', light: 'bg-green-100 text-green-800', dark: 'bg-green-900/40 text-green-300' },
  aggiornata: { label: 'Aggiorna', light: 'bg-amber-100 text-amber-800', dark: 'bg-amber-900/40 text-amber-300' },
  invariata: { label: 'Già presente', light: 'bg-gray-200 text-gray-700', dark: 'bg-slate-600 text-slate-200' },
  scartata: { label: 'Scartata', light: 'bg-red-100 text-red-800', dark: 'bg-red-900/40 text-red-300' }
};

// Import di manifestazioni e tempi dalla scheda atleta FIN Veneto (id_atleta = ID FIN dell'atleta):
// prima un'anteprima (dryRun, nessuna scrittura), poi la conferma esegue lo stesso import per davvero.
function FinImportModal({ athlete, onClose, darkMode }) {
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const runImport = async (dryRun) => {
    setLoading(true);
    setError('');
    try {
      const data = await racesApi(`/api/athletes/${athlete.id}/fin-import`, {
        method: 'POST',
        body: JSON.stringify({ dryRun })
      });
      if (dryRun) setPreview(data); else setResult(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    runImport(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [athlete.id]);

  const data = result || preview;
  // Gare raggruppate per manifestazione, nell'ordine della scheda FIN (cronologico)
  const groups = [];
  if (data) {
    for (const g of data.gare) {
      const key = `${g.data}|${g.manifestazione}`;
      let group = groups.find(x => x.key === key);
      if (!group) {
        const info = data.manifestazioni.find(m => m.nome === g.manifestazione) || {};
        group = { key, data: g.data, nome: g.manifestazione, info, gare: [] };
        groups.push(group);
      }
      group.gare.push(g);
    }
  }
  const r = data ? data.riepilogo : null;
  const nothingToDo = r && r.nuove === 0 && r.aggiornate === 0 && r.manifestazioniCreate === 0;

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center px-4 z-50">
      <div className={`w-full max-w-3xl max-h-[90vh] flex flex-col rounded-xl shadow-lg ${darkMode ? 'bg-slate-800' : 'bg-white'}`}>
        <div className={`p-5 border-b ${darkMode ? 'border-slate-700' : 'border-gray-200'}`}>
          <h3 className="text-xl font-bold">Gare da FIN Veneto — {athlete.nome} {athlete.cognome}</h3>
          <p className="text-sm opacity-70 mt-1">
            ID FIN {athlete.id_fin}
            {data && (
              <> · <a href={data.fonte} target="_blank" rel="noopener noreferrer" className="underline">scheda sorgente</a></>
            )}
          </p>
          {data && (
            <p className="text-sm mt-2">
              <strong>{data.atleta.cognomeNome}</strong>
              {data.atleta.annoNascita && <> · nato/a nel {data.atleta.annoNascita}</>}
              {data.atleta.categoria && <> · {data.atleta.categoria}</>}
              {data.atleta.societa && <> · {data.atleta.societa}</>}
              {data.atleta.codiceFin && <> · Codice F.I.N. {data.atleta.codiceFin}</>}
            </p>
          )}
        </div>

        <div className="p-5 overflow-y-auto flex-1">
          <ErrorBox message={error} darkMode={darkMode} />
          {loading && <p className="text-sm opacity-70">{preview ? 'Import in corso…' : 'Lettura della scheda FIN Veneto in corso (può richiedere qualche secondo)…'}</p>}

          {data && !loading && (
            <>
              {result ? (
                <div className={`mb-4 p-3 rounded-lg text-sm flex items-start gap-2 ${darkMode ? 'bg-green-900/40 text-green-200' : 'bg-green-50 text-green-800'}`}>
                  <CheckCircle size={16} className="shrink-0 mt-0.5" />
                  <span>
                    Import completato: {r.nuove} tempi nuovi, {r.aggiornate} aggiornati, {r.invariate} già presenti,
                    {' '}{r.scartate} scartati; {r.manifestazioniCreate} manifestazioni create.
                    {data.atleta.codiceTesseraAggiornato && ` Codice tessera FIN impostato a ${data.atleta.codiceFin}.`}
                  </span>
                </div>
              ) : (
                <p className="mb-4 text-sm">
                  Anteprima: <strong>{r.nuove}</strong> tempi nuovi, <strong>{r.aggiornate}</strong> da aggiornare,
                  {' '}<strong>{r.invariate}</strong> già presenti, <strong>{r.scartate}</strong> scartati;
                  {' '}<strong>{r.manifestazioniCreate}</strong> manifestazioni da creare.
                  {data.atleta.codiceTesseraAggiornato && ` Il codice tessera FIN verrà impostato a ${data.atleta.codiceFin}.`}
                  {' '}Nessun dato è stato ancora salvato.
                </p>
              )}

              {data.avvisi.length > 0 && (
                <ul className={`mb-4 p-3 rounded-lg text-sm list-disc list-inside ${darkMode ? 'bg-amber-900/40 text-amber-200' : 'bg-amber-50 text-amber-800'}`}>
                  {data.avvisi.map(a => <li key={a}>{a}</li>)}
                </ul>
              )}

              <p className="mb-3 text-xs opacity-60">
                Vengono importate le "Gare disputate" della scheda FIN. Posizione in classifica e compagni di staffetta
                non sono pubblicati in chiaro: le staffette hanno il solo tempo di squadra, e un compagno importato in
                seguito con lo stesso tempo viene aggiunto alla stessa staffetta.
              </p>

              <div className="space-y-3">
                {groups.map(group => (
                  <div key={group.key} className={`rounded-lg p-3 ${darkMode ? 'bg-slate-700' : 'bg-gray-100'}`}>
                    <p className="font-semibold text-sm">
                      {group.data} — {group.nome}
                    </p>
                    <p className="text-xs opacity-60 mb-2">
                      {group.info.vasca ? `Vasca ${group.info.vasca} m` : ''}
                      {group.info.luogo ? ` · ${group.info.luogo}` : ''}
                      {group.info.esistente === false ? ' · nuova manifestazione' : ' · già presente'}
                    </p>
                    <div className="space-y-1">
                      {group.gare.map((g, i) => {
                        const e = ESITI_FIN[g.esito] || ESITI_FIN.scartata;
                        return (
                          <div key={i} className="flex flex-wrap items-center gap-2 text-sm">
                            <span className="flex-1 min-w-[10rem]">{g.gara}</span>
                            <span className="font-mono">{g.tempo}</span>
                            <span className={`text-xs px-2 py-0.5 rounded-full ${darkMode ? e.dark : e.light}`}>{e.label}</span>
                            {g.motivo && <span className="w-full text-xs opacity-60">{g.motivo}</span>}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
                {groups.length === 0 && <p className="text-sm opacity-60">Nessuna gara disputata nella scheda FIN.</p>}
              </div>
            </>
          )}
        </div>

        <div className={`p-4 border-t flex justify-end gap-2 ${darkMode ? 'border-slate-700' : 'border-gray-200'}`}>
          <button
            onClick={() => onClose(!!result)}
            className={`px-4 py-2 rounded-lg ${darkMode ? 'bg-slate-600 hover:bg-slate-500' : 'bg-gray-300 hover:bg-gray-400'}`}
          >
            {result ? 'Chiudi' : 'Annulla'}
          </button>
          {preview && !result && (
            <button
              onClick={() => runImport(false)}
              disabled={loading || nothingToDo}
              className="px-4 py-2 rounded-lg bg-teal-600 hover:bg-teal-700 text-white disabled:opacity-50"
            >
              {nothingToDo ? 'Niente da importare' : 'Conferma import'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// Tab "Gare": manifestazioni, inserimento tempi, tipologie di gara e import/export Excel.
// L'admin vede tutti gli atleti (nessun filtro per categorie dell'istruttore).
function RacesTab({ athletes, darkMode }) {
  const [section, setSection] = useState('competitions'); // competitions | results | raceTypes
  const [raceTypes, setRaceTypes] = useState([]);
  const [competitions, setCompetitions] = useState([]);
  const [editingCompetition, setEditingCompetition] = useState(null); // null = chiuso, {} = nuova
  const [editingRaceType, setEditingRaceType] = useState(null);
  const [listError, setListError] = useState('');

  const [importResult, setImportResult] = useState(null);
  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);

  // Sezione Tempi
  const [selectedCompetitionId, setSelectedCompetitionId] = useState('');
  const [competitionDetail, setCompetitionDetail] = useState(null);
  const [selectedRaceTypeId, setSelectedRaceTypeId] = useState('');
  const [resultsError, setResultsError] = useState('');

  useEffect(() => {
    fetchRaceTypes();
    fetchCompetitions();
  }, []);

  const fetchRaceTypes = async () => {
    try {
      setRaceTypes(await racesApi('/api/race-types'));
    } catch (err) {
      setListError('Errore caricamento tipologie di gara: ' + err.message);
    }
  };

  const fetchCompetitions = async () => {
    try {
      setCompetitions(await racesApi('/api/competitions'));
    } catch (err) {
      setListError('Errore caricamento manifestazioni: ' + err.message);
    }
  };

  const fetchCompetitionDetail = async (id) => {
    if (!id) {
      setCompetitionDetail(null);
      return;
    }
    try {
      setCompetitionDetail(await racesApi(`/api/competitions/${id}`));
    } catch (err) {
      setCompetitionDetail(null);
      setResultsError('Errore caricamento manifestazione: ' + err.message);
    }
  };

  // In caso di errore l'eccezione arriva al modal, che la mostra senza chiudersi.
  const saveCompetition = async (data) => {
    const isEditing = Boolean(data.id);
    await racesApi(`/api/competitions${isEditing ? `/${data.id}` : ''}`, {
      method: isEditing ? 'PUT' : 'POST',
      body: JSON.stringify(data)
    });
    setEditingCompetition(null);
    await fetchCompetitions();
    if (isEditing && String(data.id) === String(selectedCompetitionId)) {
      await fetchCompetitionDetail(selectedCompetitionId);
    }
  };

  const deleteCompetition = async (competition) => {
    if (!window.confirm(
      `Eliminare la manifestazione "${competition.nome}" del ${formatDataGara(competition.data)}? Verranno cancellati anche tutti i tempi registrati (${competition.tempi_count || 0}). L'operazione non è reversibile.`
    )) return;
    try {
      setListError('');
      await racesApi(`/api/competitions/${competition.id}`, { method: 'DELETE' });
      if (String(competition.id) === String(selectedCompetitionId)) {
        setSelectedCompetitionId('');
        setSelectedRaceTypeId('');
        setCompetitionDetail(null);
      }
      await fetchCompetitions();
    } catch (err) {
      setListError('Errore eliminazione manifestazione: ' + err.message);
    }
  };

  const saveRaceType = async (data) => {
    const isEditing = Boolean(data.id);
    await racesApi(`/api/race-types${isEditing ? `/${data.id}` : ''}`, {
      method: isEditing ? 'PUT' : 'POST',
      body: JSON.stringify(data)
    });
    setEditingRaceType(null);
    await fetchRaceTypes();
  };

  const deleteRaceType = async (raceType) => {
    if (!window.confirm(`Eliminare la tipologia di gara "${raceType.nome}"?`)) return;
    try {
      setListError('');
      await racesApi(`/api/race-types/${raceType.id}`, { method: 'DELETE' });
      await fetchRaceTypes();
    } catch (err) {
      setListError('Errore eliminazione tipologia di gara: ' + err.message);
    }
  };

  const importCompetitionsFile = async (file) => {
    if (!file) return;
    try {
      setImporting(true);
      setImportResult(null);
      const formData = new FormData();
      formData.append('file', file);
      const data = await racesApi('/api/competitions/import', { method: 'POST', body: formData });
      setImportResult(data);
      await Promise.all([fetchCompetitions(), fetchRaceTypes()]);
      if (selectedCompetitionId) await fetchCompetitionDetail(selectedCompetitionId);
    } catch (err) {
      alert('Errore importazione: ' + err.message);
    } finally {
      setImporting(false);
    }
  };

  const downloadCompetitionsExport = async () => {
    try {
      setExporting(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/competitions/export`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        alert('Errore scaricamento: ' + (err.error || response.statusText));
        return;
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'manifestazioni-tempi.xlsx';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert('Errore scaricamento: ' + err.message);
    } finally {
      setExporting(false);
    }
  };

  const openResults = (competitionId) => {
    setSelectedCompetitionId(String(competitionId));
    setSelectedRaceTypeId('');
    setResultsError('');
    setSection('results');
    fetchCompetitionDetail(competitionId);
  };

  const sectionButton = (key, label) => (
    <button
      type="button"
      onClick={() => {
        setSection(key);
        if (key === 'results' && selectedCompetitionId) fetchCompetitionDetail(selectedCompetitionId);
      }}
      className={`flex-1 py-2 px-3 text-sm font-medium ${
        section === key
          ? 'bg-blue-600 text-white'
          : darkMode ? 'bg-slate-800 hover:bg-slate-700' : 'bg-white hover:bg-gray-100'
      }`}
    >
      {label}
    </button>
  );

  const selectedRaceType = competitionDetail && competitionDetail.gare
    ? competitionDetail.gare.find(g => String(g.id) === String(selectedRaceTypeId))
    : null;
  const raceResults = competitionDetail && selectedRaceType
    ? (competitionDetail.results || []).filter(r => r.race_type_id === selectedRaceType.id)
    : [];
  const reloadResults = () => Promise.all([fetchCompetitionDetail(competitionDetail.id), fetchCompetitions()]);
  const inputClass = `w-full px-3 py-2 rounded-lg border ${darkMode ? 'bg-slate-700 border-slate-600' : 'bg-gray-100 border-gray-300'} focus:outline-none focus:ring-2 focus:ring-blue-500`;
  const secondaryButton = `px-3 py-2 rounded-lg text-sm ${darkMode ? 'bg-slate-600 hover:bg-slate-500' : 'bg-gray-300 hover:bg-gray-400'}`;

  return (
    <div>
      {/* Import / Export Excel (stesso layout della tab Presenze) */}
      <div className="flex flex-col sm:flex-row gap-2 mb-4">
        <label
          className={`flex-1 cursor-pointer px-4 py-3 rounded-lg flex items-center gap-2 justify-center font-medium ${
            darkMode ? 'bg-slate-700 hover:bg-slate-600' : 'bg-gray-200 hover:bg-gray-300'
          } ${importing ? 'opacity-60 cursor-not-allowed' : ''}`}
        >
          <Upload size={20} />
          {importing ? 'Importazione...' : 'Importa da Excel'}
          <input
            type="file"
            accept=".xlsx,.xls"
            disabled={importing}
            onChange={(e) => {
              const file = e.target.files && e.target.files[0];
              importCompetitionsFile(file);
              e.target.value = '';
            }}
            className="hidden"
          />
        </label>
        <button
          onClick={downloadCompetitionsExport}
          disabled={exporting}
          className={`flex-1 px-4 py-3 rounded-lg flex items-center gap-2 justify-center font-medium text-white ${
            darkMode ? 'bg-emerald-700 hover:bg-emerald-600' : 'bg-emerald-600 hover:bg-emerald-700'
          } ${exporting ? 'opacity-60 cursor-not-allowed' : ''}`}
        >
          <Download size={20} />
          {exporting ? 'Download...' : 'Esporta Excel'}
        </button>
      </div>

      {importResult && (
        <div className={`mb-4 p-4 rounded-lg ${darkMode ? 'bg-slate-700' : 'bg-gray-100'}`}>
          <p className="font-semibold mb-1">Risultato importazione</p>
          <p className="text-sm">
            Importate: {importResult.imported} — Saltate: {importResult.skipped} — Manifestazioni create: {importResult.competitionsCreated || 0}
          </p>
          {importResult.errors && importResult.errors.length > 0 && (
            <div className="mt-2 space-y-1 max-h-64 overflow-y-auto">
              {importResult.errors.map((e, idx) => (
                <p key={idx} className={`text-xs ${darkMode ? 'text-amber-300' : 'text-amber-700'}`}>
                  Riga {e.row}: {e.reason}
                </p>
              ))}
            </div>
          )}
          <button
            onClick={() => setImportResult(null)}
            className={`mt-3 px-3 py-1.5 rounded-lg text-xs ${darkMode ? 'bg-slate-600 hover:bg-slate-500' : 'bg-gray-300 hover:bg-gray-400'}`}
          >
            Chiudi
          </button>
        </div>
      )}

      <p className={`text-xs mb-4 ${darkMode ? 'text-slate-500' : 'text-gray-400'}`}>
        Esporta o importa manifestazioni e tempi in formato Excel. Il file ha tre fogli: <strong>Manifestazioni</strong> (Manifestazione, Data, Luogo, Vasca, Tipo, Note, Gare — codici gara separati da virgola), <strong>Tempi</strong> (Manifestazione, Data, Luogo, Vasca, Tipo, Gara, Squadra, Frazione, Nome, Cognome, Codice tessera FIN, ID FIN, Tempo, Posizione, Stato — una riga per frazionista nelle staffette; tempo nel formato MM:SS,cc, stato SQ/RT/NP) e <strong>Gare FIN</strong> (elenco di riferimento dei codici gara). Un file con un solo foglio viene letto come "Tempi". In import le manifestazioni mancanti vengono create al volo (abbinate per nome + data) e le gare mancanti aggiunte alla manifestazione; ogni atleta viene cercato per ID FIN, poi per codice tessera FIN, infine per nome e cognome. Le righe senza corrispondenza vengono segnalate e saltate, senza alterare il resto dei dati. Un file esportato da qui è re-importabile invariato senza creare duplicati.
      </p>

      <div className={`flex gap-0 mb-4 rounded-lg overflow-hidden border ${darkMode ? 'border-slate-700' : 'border-gray-300'}`}>
        {sectionButton('competitions', 'Manifestazioni')}
        {sectionButton('results', 'Tempi')}
        {sectionButton('raceTypes', 'Tipologie gara')}
      </div>

      <ErrorBox message={listError} onClose={() => setListError('')} darkMode={darkMode} />

      {section === 'competitions' && (
        <div>
          <button
            onClick={() => setEditingCompetition({})}
            className="w-full mb-4 bg-blue-600 hover:bg-blue-700 text-white px-4 py-3 rounded-lg flex items-center gap-2 justify-center"
          >
            <Plus size={20} />
            Nuova Manifestazione
          </button>
          <div className="space-y-3">
            {competitions.length === 0 ? (
              <p className="text-center py-8 opacity-50">Nessuna manifestazione</p>
            ) : (
              competitions.map(c => (
                <div key={c.id} className={`p-4 rounded-lg ${darkMode ? 'bg-slate-700' : 'bg-gray-100'}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="font-semibold">{c.nome}</p>
                      <p className="text-sm opacity-60">
                        {formatDataGara(c.data)}{c.luogo ? ` — ${c.luogo}` : ''}
                      </p>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {c.tipo && (
                          <span className={`text-xs px-2 py-0.5 rounded-full ${darkMode ? 'bg-purple-900/40 text-purple-300' : 'bg-purple-100 text-purple-700'}`}>
                            {c.tipo}
                          </span>
                        )}
                        <span className={`text-xs px-2 py-0.5 rounded-full ${darkMode ? 'bg-sky-900/40 text-sky-300' : 'bg-sky-100 text-sky-700'}`}>
                          Vasca {c.vasca} m
                        </span>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${darkMode ? 'bg-amber-900/40 text-amber-300' : 'bg-amber-100 text-amber-700'}`}>
                          {c.gare_count || 0} gare
                        </span>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${darkMode ? 'bg-green-900/40 text-green-300' : 'bg-green-100 text-green-700'}`}>
                          {c.tempi_count || 0} tempi
                        </span>
                      </div>
                      {c.note && <p className="text-xs opacity-60 mt-1">{c.note}</p>}
                    </div>
                    <div className="flex flex-col sm:flex-row gap-2 shrink-0">
                      <button onClick={() => openResults(c.id)} className={secondaryButton}>
                        Tempi
                      </button>
                      <button onClick={() => setEditingCompetition(c)} className={secondaryButton}>
                        Modifica
                      </button>
                      <button
                        onClick={() => deleteCompetition(c)}
                        className="px-3 py-2 rounded-lg text-sm bg-red-600 hover:bg-red-700 text-white"
                      >
                        Elimina
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {section === 'results' && (
        <div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-4">
            <div>
              <label className="block text-xs opacity-60 mb-1">Manifestazione</label>
              <select
                value={selectedCompetitionId}
                onChange={(e) => {
                  setSelectedCompetitionId(e.target.value);
                  setSelectedRaceTypeId('');
                  setResultsError('');
                  fetchCompetitionDetail(e.target.value);
                }}
                className={inputClass}
              >
                <option value="">— Scegli una manifestazione —</option>
                {competitions.map(c => (
                  <option key={c.id} value={c.id}>
                    {formatDataGara(c.data)} — {c.nome} ({c.vasca} m)
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs opacity-60 mb-1">Gara</label>
              <select
                value={selectedRaceTypeId}
                onChange={(e) => { setSelectedRaceTypeId(e.target.value); setResultsError(''); }}
                disabled={!competitionDetail}
                className={inputClass}
              >
                <option value="">— Scegli una gara —</option>
                {competitionDetail && (competitionDetail.gare || []).map(g => {
                  const n = (competitionDetail.results || []).filter(r => r.race_type_id === g.id).length;
                  return (
                    <option key={g.id} value={g.id}>
                      {g.nome}{n > 0 ? ` (${n})` : ''}
                    </option>
                  );
                })}
              </select>
            </div>
          </div>

          <ErrorBox message={resultsError} onClose={() => setResultsError('')} darkMode={darkMode} />

          {!competitionDetail && (
            <p className="text-center py-8 opacity-50">Scegli una manifestazione per inserire i tempi</p>
          )}
          {competitionDetail && (competitionDetail.gare || []).length === 0 && (
            <p className="text-center py-8 opacity-50">Nessuna gara prevista: aggiungile modificando la manifestazione</p>
          )}
          {competitionDetail && (competitionDetail.gare || []).length > 0 && !selectedRaceType && (
            <p className="text-center py-8 opacity-50">Scegli una gara</p>
          )}

          {selectedRaceType && !selectedRaceType.staffetta && (
            <IndividualResultsSection
              competition={competitionDetail}
              raceType={selectedRaceType}
              results={raceResults}
              athletes={athletes}
              onChanged={reloadResults}
              darkMode={darkMode}
            />
          )}

          {selectedRaceType && selectedRaceType.staffetta && (
            <RelayResultsSection
              key={selectedRaceType.id}
              competition={competitionDetail}
              raceType={selectedRaceType}
              results={raceResults}
              athletes={athletes}
              onChanged={reloadResults}
              darkMode={darkMode}
            />
          )}
        </div>
      )}

      {section === 'raceTypes' && (
        <div>
          <button
            onClick={() => setEditingRaceType({})}
            className="w-full mb-4 bg-blue-600 hover:bg-blue-700 text-white px-4 py-3 rounded-lg flex items-center gap-2 justify-center"
          >
            <Plus size={20} />
            Aggiungi Tipologia
          </button>
          <p className={`text-xs mb-3 ${darkMode ? 'text-slate-500' : 'text-gray-400'}`}>
            Le tipologie di gara (codici FIN) sono selezionabili nelle manifestazioni. Una tipologia già prevista in una manifestazione non può essere eliminata.
          </p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className={`text-left ${darkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                  <th className="py-2 pr-2">Codice</th>
                  <th className="py-2 pr-2">Nome</th>
                  <th className="py-2 pr-2">Stile</th>
                  <th className="py-2 pr-2">Distanza</th>
                  <th className="py-2 pr-2">Staffetta</th>
                  <th className="py-2 pr-2">Mistaffetta</th>
                  <th className="py-2 pr-2">Solo vasca 25</th>
                  <th className="py-2"></th>
                </tr>
              </thead>
              <tbody>
                {raceTypes.map(rt => (
                  <tr key={rt.id} className={`border-t ${darkMode ? 'border-slate-700' : 'border-gray-200'}`}>
                    <td className="py-2 pr-2 font-mono">{rt.codice}</td>
                    <td className="py-2 pr-2">{rt.nome}</td>
                    <td className="py-2 pr-2">{rt.stile}</td>
                    <td className="py-2 pr-2">{rt.distanza}</td>
                    <td className="py-2 pr-2">{rt.staffetta ? 'Sì' : '—'}</td>
                    <td className="py-2 pr-2">{rt.mistaffetta ? 'Sì' : '—'}</td>
                    <td className="py-2 pr-2">{rt.solo_vasca_25 ? 'Sì' : '—'}</td>
                    <td className="py-2">
                      <div className="flex gap-2 justify-end">
                        <button
                          onClick={() => setEditingRaceType(rt)}
                          className={`px-2 py-1 rounded-lg text-xs ${darkMode ? 'bg-slate-600 hover:bg-slate-500' : 'bg-gray-300 hover:bg-gray-400'}`}
                        >
                          Modifica
                        </button>
                        <button
                          onClick={() => deleteRaceType(rt)}
                          className="px-2 py-1 rounded-lg text-xs bg-red-600 hover:bg-red-700 text-white"
                        >
                          Elimina
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {raceTypes.length === 0 && <p className="text-center py-8 opacity-50">Nessuna tipologia di gara</p>}
          </div>
        </div>
      )}

      {editingCompetition !== null && (
        <CompetitionFormModal
          competition={editingCompetition}
          raceTypes={raceTypes}
          onSave={saveCompetition}
          onCancel={() => setEditingCompetition(null)}
          darkMode={darkMode}
        />
      )}

      {editingRaceType !== null && (
        <RaceTypeFormModal
          raceType={editingRaceType}
          onSave={saveRaceType}
          onCancel={() => setEditingRaceType(null)}
          darkMode={darkMode}
        />
      )}
    </div>
  );
}

// Gara individuale: una riga per atleta (tutti gli atleti), con tempo/posizione/stato.
function IndividualResultsSection({ competition, raceType, results, athletes, onChanged, darkMode }) {
  const [search, setSearch] = useState('');
  const [onlyWithResult, setOnlyWithResult] = useState(false);

  const resultByAthlete = new Map();
  results.forEach(r => {
    const a = r.atleti && r.atleti[0];
    if (a) resultByAthlete.set(a.athlete_id, r);
  });

  const term = search.trim().toLowerCase();
  const rows = [...athletes]
    .sort((a, b) => `${a.cognome} ${a.nome}`.localeCompare(`${b.cognome} ${b.nome}`, 'it'))
    .filter(a => !term || `${a.nome} ${a.cognome} ${a.nome}`.toLowerCase().includes(term))
    .filter(a => !onlyWithResult || resultByAthlete.has(a.id));

  return (
    <div>
      <p className="font-semibold mb-2">
        {raceType.nome} <span className="text-xs font-normal opacity-60">— {results.length} tempi registrati</span>
      </p>
      <div className="flex flex-col sm:flex-row gap-2 mb-3">
        <input
          type="text"
          placeholder="Cerca atleta..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className={`flex-1 px-3 py-2 rounded-lg border ${darkMode ? 'bg-slate-700 border-slate-600' : 'bg-gray-100 border-gray-300'} focus:outline-none focus:ring-2 focus:ring-blue-500`}
        />
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={onlyWithResult} onChange={(e) => setOnlyWithResult(e.target.checked)} />
          Solo con tempo
        </label>
      </div>
      <div className="space-y-2">
        {rows.length === 0 ? (
          <p className="text-center py-8 opacity-50">Nessun atleta</p>
        ) : (
          rows.map(a => {
            const r = resultByAthlete.get(a.id);
            return (
              <IndividualResultRow
                // La key cambia quando il risultato salvato cambia: la riga si reinizializza dai dati del server.
                key={`${raceType.id}-${a.id}-${r ? `${r.id}-${r.tempo_centesimi}-${r.posizione}-${r.stato}` : 'new'}`}
                competitionId={competition.id}
                raceTypeId={raceType.id}
                athlete={a}
                result={r}
                onChanged={onChanged}
                darkMode={darkMode}
              />
            );
          })
        )}
      </div>
    </div>
  );
}

function IndividualResultRow({ competitionId, raceTypeId, athlete, result, onChanged, darkMode }) {
  const [tempo, setTempo] = useState(result ? (result.tempo || formatTempo(result.tempo_centesimi)) : '');
  const [posizione, setPosizione] = useState(result && result.posizione ? String(result.posizione) : '');
  const [stato, setStato] = useState(result && result.stato ? result.stato : '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const save = async () => {
    setError('');
    if (tempo.trim() && !isValidTempo(tempo)) {
      setError('Tempo non valido: usa il formato MM:SS,cc');
      return;
    }
    if (!tempo.trim() && !stato) {
      setError('Indica un tempo oppure uno stato (SQ, RT, NP)');
      return;
    }
    const body = {
      athlete_ids: [athlete.id],
      tempo: tempo.trim() || null,
      posizione: posizione.trim() || null,
      stato: stato || null
    };
    try {
      setSaving(true);
      if (result) {
        await racesApi(`/api/results/${result.id}`, { method: 'PUT', body: JSON.stringify(body) });
      } else {
        await racesApi(`/api/competitions/${competitionId}/results`, {
          method: 'POST',
          body: JSON.stringify({ ...body, race_type_id: raceTypeId })
        });
      }
      await onChanged();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!result) return;
    if (!window.confirm(`Eliminare il tempo di ${athlete.nome} ${athlete.cognome}?`)) return;
    try {
      setSaving(true);
      setError('');
      await racesApi(`/api/results/${result.id}`, { method: 'DELETE' });
      await onChanged();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const fieldClass = `px-2 py-1.5 rounded-lg border text-sm ${darkMode ? 'bg-slate-800 border-slate-600' : 'bg-white border-gray-300'} focus:outline-none focus:ring-2 focus:ring-blue-500`;

  return (
    <div className={`p-3 rounded-lg ${darkMode ? 'bg-slate-700' : 'bg-gray-100'} ${result ? (darkMode ? 'ring-1 ring-green-700' : 'ring-1 ring-green-300') : ''}`}>
      <div className="flex flex-col md:flex-row md:items-center gap-2">
        <div className="md:w-48 shrink-0">
          <p className="font-medium text-sm">{athlete.cognome} {athlete.nome}</p>
          {athlete.categoria && <p className="text-xs opacity-60">{athlete.categoria}</p>}
        </div>
        <div className="flex flex-wrap items-center gap-2 flex-1">
          <input
            type="text"
            inputMode="decimal"
            placeholder="MM:SS,cc"
            value={tempo}
            onChange={(e) => setTempo(e.target.value)}
            className={`${fieldClass} font-mono w-28`}
          />
          <input
            type="number"
            min="1"
            placeholder="Pos."
            value={posizione}
            onChange={(e) => setPosizione(e.target.value)}
            className={`${fieldClass} w-20`}
          />
          <select value={stato} onChange={(e) => setStato(e.target.value)} className={fieldClass} title="Stato">
            <option value="">—</option>
            {STATI_GARA.map(s => (
              <option key={s.value} value={s.value}>{s.value}</option>
            ))}
          </select>
          <button
            type="button"
            onClick={save}
            disabled={saving}
            className={`px-3 py-1.5 rounded-lg text-sm bg-blue-600 hover:bg-blue-700 text-white ${saving ? 'opacity-60 cursor-not-allowed' : ''}`}
          >
            {saving ? '...' : 'Salva'}
          </button>
          {result && (
            <button
              type="button"
              onClick={remove}
              disabled={saving}
              className="px-3 py-1.5 rounded-lg text-sm bg-red-600 hover:bg-red-700 text-white"
            >
              Elimina
            </button>
          )}
        </div>
      </div>
      {error && <p className={`text-xs mt-2 ${darkMode ? 'text-red-300' : 'text-red-700'}`}>{error}</p>}
    </div>
  );
}

// Staffetta: elenco staffette registrate + form (nuova o modifica) con 4 frazionisti ordinati.
const EMPTY_RELAY_FORM = { id: null, squadra: '', frazionisti: ['', '', '', ''], tempo: '', posizione: '', stato: '' };

function RelayResultsSection({ competition, raceType, results, athletes, onChanged, darkMode }) {
  const [form, setForm] = useState(EMPTY_RELAY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const sortedAthletes = [...athletes].sort((a, b) =>
    `${a.cognome} ${a.nome}`.localeCompare(`${b.cognome} ${b.nome}`, 'it')
  );
  const sortedAtleti = (r) => [...(r.atleti || [])].sort((a, b) => a.frazione - b.frazione);

  const editRelay = (r) => {
    const frazionisti = ['', '', '', ''];
    sortedAtleti(r).forEach((a, idx) => {
      const pos = a.frazione ? a.frazione - 1 : idx;
      if (pos >= 0 && pos < 4) frazionisti[pos] = String(a.athlete_id);
    });
    setForm({
      id: r.id,
      squadra: r.squadra || '',
      frazionisti,
      tempo: r.tempo || formatTempo(r.tempo_centesimi),
      posizione: r.posizione ? String(r.posizione) : '',
      stato: r.stato || ''
    });
    setError('');
  };

  const setFrazionista = (idx, value) => {
    const frazionisti = [...form.frazionisti];
    frazionisti[idx] = value;
    setForm({ ...form, frazionisti });
  };

  const save = async (e) => {
    e.preventDefault();
    setError('');
    // I frazionisti vanno in ordine di frazione: i campi vuoti vengono saltati.
    const ids = form.frazionisti.filter(Boolean).map(Number);
    if (ids.length === 0) {
      setError('Indica almeno un frazionista');
      return;
    }
    if (new Set(ids).size !== ids.length) {
      setError('Lo stesso atleta compare più volte');
      return;
    }
    if (form.tempo.trim() && !isValidTempo(form.tempo)) {
      setError('Tempo non valido: usa il formato MM:SS,cc');
      return;
    }
    if (!form.tempo.trim() && !form.stato) {
      setError('Indica un tempo oppure uno stato (SQ, RT, NP)');
      return;
    }
    const body = {
      athlete_ids: ids,
      tempo: form.tempo.trim() || null,
      posizione: form.posizione.trim() || null,
      stato: form.stato || null,
      squadra: form.squadra.trim() || null
    };
    try {
      setSaving(true);
      if (form.id) {
        await racesApi(`/api/results/${form.id}`, { method: 'PUT', body: JSON.stringify(body) });
      } else {
        await racesApi(`/api/competitions/${competition.id}/results`, {
          method: 'POST',
          body: JSON.stringify({ ...body, race_type_id: raceType.id })
        });
      }
      setForm(EMPTY_RELAY_FORM);
      await onChanged();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (r) => {
    if (!window.confirm(`Eliminare la staffetta${r.squadra ? ` "${r.squadra}"` : ''}?`)) return;
    try {
      setError('');
      await racesApi(`/api/results/${r.id}`, { method: 'DELETE' });
      if (form.id === r.id) setForm(EMPTY_RELAY_FORM);
      await onChanged();
    } catch (err) {
      setError(err.message);
    }
  };

  const fieldClass = `w-full px-3 py-2 rounded-lg border text-sm ${darkMode ? 'bg-slate-800 border-slate-600' : 'bg-white border-gray-300'} focus:outline-none focus:ring-2 focus:ring-blue-500`;
  const inlineFieldClass = `px-3 py-2 rounded-lg border text-sm ${darkMode ? 'bg-slate-800 border-slate-600' : 'bg-white border-gray-300'} focus:outline-none focus:ring-2 focus:ring-blue-500`;

  return (
    <div>
      <p className="font-semibold mb-2">
        {raceType.nome} <span className="text-xs font-normal opacity-60">— {results.length} staffette registrate</span>
      </p>

      <div className="space-y-2 mb-4">
        {results.length === 0 ? (
          <p className="text-center py-4 opacity-50">Nessuna staffetta registrata</p>
        ) : (
          results.map(r => (
            <div key={r.id} className={`p-3 rounded-lg ${darkMode ? 'bg-slate-700' : 'bg-gray-100'}`}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="font-medium text-sm">
                    {r.squadra || 'Staffetta'}
                    {' — '}
                    <span className="font-mono">{r.tempo || '—'}</span>
                    {r.stato && <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-red-600 text-white">{r.stato}</span>}
                    {r.posizione && <span className="ml-2 text-xs opacity-70">{r.posizione}° posto</span>}
                  </p>
                  <p className="text-xs opacity-70 mt-1">
                    {sortedAtleti(r).map(a => `${a.frazione}. ${a.nome} ${a.cognome}`).join(' · ')}
                  </p>
                </div>
                <div className="flex gap-2 shrink-0">
                  <button
                    onClick={() => editRelay(r)}
                    className={`px-3 py-1.5 rounded-lg text-sm ${darkMode ? 'bg-slate-600 hover:bg-slate-500' : 'bg-gray-300 hover:bg-gray-400'}`}
                  >
                    Modifica
                  </button>
                  <button
                    onClick={() => remove(r)}
                    className="px-3 py-1.5 rounded-lg text-sm bg-red-600 hover:bg-red-700 text-white"
                  >
                    Elimina
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      <form onSubmit={save} className={`p-4 rounded-lg space-y-3 ${darkMode ? 'bg-slate-700' : 'bg-gray-100'}`}>
        <p className="font-semibold text-sm">{form.id ? 'Modifica staffetta' : 'Nuova staffetta'}</p>
        <input
          type="text"
          placeholder="Squadra (es. Società A)"
          value={form.squadra}
          onChange={(e) => setForm({ ...form, squadra: e.target.value })}
          className={fieldClass}
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {form.frazionisti.map((value, idx) => (
            <div key={idx}>
              <label className="block text-xs opacity-60 mb-1">Frazionista {idx + 1}</label>
              <select value={value} onChange={(e) => setFrazionista(idx, e.target.value)} className={fieldClass}>
                <option value="">— Nessuno —</option>
                {sortedAthletes.map(a => (
                  <option key={a.id} value={a.id}>
                    {a.cognome} {a.nome}{a.categoria ? ` (${a.categoria})` : ''}
                  </option>
                ))}
              </select>
            </div>
          ))}
        </div>
        <div className="flex flex-wrap gap-2">
          <input
            type="text"
            inputMode="decimal"
            placeholder="MM:SS,cc"
            value={form.tempo}
            onChange={(e) => setForm({ ...form, tempo: e.target.value })}
            className={`${inlineFieldClass} font-mono w-32`}
          />
          <input
            type="number"
            min="1"
            placeholder="Pos."
            value={form.posizione}
            onChange={(e) => setForm({ ...form, posizione: e.target.value })}
            className={`${inlineFieldClass} w-24`}
          />
          <select
            value={form.stato}
            onChange={(e) => setForm({ ...form, stato: e.target.value })}
            className={inlineFieldClass}
          >
            <option value="">— Stato —</option>
            {STATI_GARA.map(s => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
        </div>
        {error && <p className={`text-xs ${darkMode ? 'text-red-300' : 'text-red-700'}`}>{error}</p>}
        <div className="flex gap-2">
          {form.id && (
            <button
              type="button"
              onClick={() => { setForm(EMPTY_RELAY_FORM); setError(''); }}
              className={`flex-1 px-4 py-2 rounded-lg font-semibold ${darkMode ? 'bg-slate-600 hover:bg-slate-500' : 'bg-gray-300 hover:bg-gray-400'}`}
            >
              Annulla modifica
            </button>
          )}
          <button
            type="submit"
            disabled={saving}
            className={`flex-1 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 rounded-lg ${saving ? 'opacity-60 cursor-not-allowed' : ''}`}
          >
            {saving ? 'Salvataggio...' : 'Salva staffetta'}
          </button>
        </div>
      </form>
    </div>
  );
}

function CompetitionFormModal({ competition, raceTypes, onSave, onCancel, darkMode }) {
  const isEditing = Boolean(competition.id);
  const [nome, setNome] = useState(competition.nome || '');
  const [data, setData] = useState(competition.data ? String(competition.data).slice(0, 10) : '');
  const [luogo, setLuogo] = useState(competition.luogo || '');
  const [vasca, setVasca] = useState(competition.vasca ? Number(competition.vasca) : 25);
  const [tipo, setTipo] = useState(competition.tipo || '');
  const [note, setNote] = useState(competition.note || '');
  const [selected, setSelected] = useState(() => new Set((competition.gare || []).map(Number)));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // In vasca da 50 le gare solo_vasca_25 non sono ammesse.
  const isDisabled = (rt) => vasca === 50 && rt.solo_vasca_25;

  const changeVasca = (value) => {
    setVasca(value);
    if (value === 50) {
      setSelected(prev => new Set([...prev].filter(id => {
        const rt = raceTypes.find(r => r.id === id);
        return !(rt && rt.solo_vasca_25);
      })));
    }
  };

  const toggle = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  // Aggiunge alla selezione tutte le gare che soddisfano il criterio (escluse quelle non ammesse).
  const selectAll = (predicate) => {
    setSelected(prev => {
      const next = new Set(prev);
      raceTypes.filter(rt => predicate(rt) && !isDisabled(rt)).forEach(rt => next.add(rt.id));
      return next;
    });
  };

  // Gruppi: individuali per stile (ordine STILI_GARA, poi eventuali stili non previsti),
  // staffette e mistaffette.
  const individuali = raceTypes.filter(rt => !rt.staffetta);
  const stili = [
    ...STILI_GARA.filter(s => individuali.some(rt => rt.stile === s)),
    ...[...new Set(individuali.map(rt => rt.stile))].filter(s => !STILI_GARA.includes(s))
  ];
  const groups = [
    ...stili.map(s => ({ label: s ? s.charAt(0).toUpperCase() + s.slice(1) : 'Altro', items: individuali.filter(rt => rt.stile === s) })),
    { label: 'Staffette', items: raceTypes.filter(rt => rt.staffetta && !rt.mistaffetta) },
    { label: 'Mistaffette', items: raceTypes.filter(rt => rt.staffetta && rt.mistaffetta) }
  ].filter(g => g.items.length > 0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!nome.trim() || !data) return;
    setError('');
    try {
      setSaving(true);
      await onSave({
        ...(isEditing ? { id: competition.id } : {}),
        nome: nome.trim(),
        data,
        luogo: luogo.trim() || null,
        vasca,
        tipo: tipo.trim() || null,
        note: note.trim() || null,
        gare: raceTypes.filter(rt => selected.has(rt.id) && !isDisabled(rt)).map(rt => rt.id)
      });
    } catch (err) {
      // Es. "rimozione di una gara che ha già tempi", nome+data duplicati: il modal resta aperto.
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const inputClass = `w-full px-4 py-3 rounded-lg border ${darkMode ? 'bg-slate-700 border-slate-600' : 'bg-gray-100 border-gray-300'} focus:outline-none focus:ring-2 focus:ring-blue-500`;
  const smallButton = `px-3 py-1.5 rounded-lg text-xs ${darkMode ? 'bg-slate-700 hover:bg-slate-600' : 'bg-gray-200 hover:bg-gray-300'}`;
  const selectedCount = raceTypes.filter(rt => selected.has(rt.id) && !isDisabled(rt)).length;

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center px-4 z-50">
      <div className={`w-full max-w-2xl p-6 rounded-xl shadow-lg ${darkMode ? 'bg-slate-800' : 'bg-white'} max-h-[90vh] overflow-y-auto`}>
        <h3 className="text-xl font-bold mb-4">
          {isEditing ? 'Modifica Manifestazione' : 'Nuova Manifestazione'}
        </h3>

        <form onSubmit={handleSubmit} className="space-y-4">
          <input
            type="text"
            placeholder="Nome manifestazione"
            value={nome}
            onChange={(e) => setNome(e.target.value)}
            required
            className={inputClass}
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs opacity-60 mb-1">Data</label>
              <input type="date" value={data} onChange={(e) => setData(e.target.value)} required className={inputClass} />
            </div>
            <div>
              <label className="block text-xs opacity-60 mb-1">Vasca</label>
              <select value={vasca} onChange={(e) => changeVasca(Number(e.target.value))} className={inputClass}>
                <option value={25}>25 m (vasca corta)</option>
                <option value={50}>50 m (vasca lunga)</option>
              </select>
            </div>
          </div>
          <input
            type="text"
            placeholder="Luogo"
            value={luogo}
            onChange={(e) => setLuogo(e.target.value)}
            className={inputClass}
          />
          <div>
            <label className="block text-xs opacity-60 mb-1">Tipo</label>
            <input
              type="text"
              list="tipi-manifestazione"
              placeholder="Es. Regionale, Trofeo, Meeting..."
              value={tipo}
              onChange={(e) => setTipo(e.target.value)}
              className={inputClass}
            />
            <datalist id="tipi-manifestazione">
              {TIPI_MANIFESTAZIONE.map(t => <option key={t} value={t} />)}
            </datalist>
          </div>
          <textarea
            placeholder="Note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={2}
            className={inputClass}
          />

          <div>
            <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
              <p className="text-sm font-semibold">Gare previste ({selectedCount})</p>
              <div className="flex flex-wrap gap-2">
                <button type="button" onClick={() => selectAll(rt => !rt.staffetta)} className={smallButton}>
                  Seleziona tutte le individuali
                </button>
                <button type="button" onClick={() => selectAll(rt => rt.staffetta)} className={smallButton}>
                  Tutte le staffette
                </button>
                <button type="button" onClick={() => setSelected(new Set())} className={smallButton}>
                  Nessuna
                </button>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {groups.map(g => (
                <div key={g.label} className={`p-3 rounded-lg ${darkMode ? 'bg-slate-700' : 'bg-gray-100'}`}>
                  <p className="text-xs font-semibold uppercase opacity-70 mb-1">{g.label}</p>
                  {g.items.map(rt => (
                    <label
                      key={rt.id}
                      className={`flex items-center gap-2 text-sm py-0.5 ${isDisabled(rt) ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}`}
                      title={isDisabled(rt) ? 'Gara ammessa solo in vasca da 25 m' : undefined}
                    >
                      <input
                        type="checkbox"
                        checked={selected.has(rt.id) && !isDisabled(rt)}
                        disabled={isDisabled(rt)}
                        onChange={() => toggle(rt.id)}
                      />
                      {rt.nome}
                      {rt.solo_vasca_25 && <span className="text-xs opacity-60">(solo 25 m)</span>}
                    </label>
                  ))}
                </div>
              ))}
            </div>
          </div>

          <ErrorBox message={error} darkMode={darkMode} />

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onCancel}
              className={`flex-1 px-4 py-3 rounded-lg font-semibold ${darkMode ? 'bg-slate-700 hover:bg-slate-600' : 'bg-gray-200 hover:bg-gray-300'}`}
            >
              Annulla
            </button>
            <button
              type="submit"
              disabled={saving}
              className={`flex-1 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 rounded-lg transition ${saving ? 'opacity-60 cursor-not-allowed' : ''}`}
            >
              {saving ? 'Salvataggio...' : 'Salva'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function RaceTypeFormModal({ raceType, onSave, onCancel, darkMode }) {
  const isEditing = Boolean(raceType.id);
  const [codice, setCodice] = useState(raceType.codice || '');
  const [nome, setNome] = useState(raceType.nome || '');
  const [stile, setStile] = useState(raceType.stile || '');
  const [distanza, setDistanza] = useState(raceType.distanza ? String(raceType.distanza) : '');
  const [staffetta, setStaffetta] = useState(Boolean(raceType.staffetta));
  const [mistaffetta, setMistaffetta] = useState(Boolean(raceType.mistaffetta));
  const [soloVasca25, setSoloVasca25] = useState(Boolean(raceType.solo_vasca_25));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!codice.trim() || !nome.trim() || !stile.trim() || !distanza) return;
    setError('');
    try {
      setSaving(true);
      await onSave({
        ...(isEditing ? { id: raceType.id } : {}),
        codice: codice.trim(),
        nome: nome.trim(),
        stile: stile.trim(),
        distanza: Number(distanza),
        staffetta,
        mistaffetta: staffetta && mistaffetta,
        solo_vasca_25: soloVasca25
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const inputClass = `w-full px-4 py-3 rounded-lg border ${darkMode ? 'bg-slate-700 border-slate-600' : 'bg-gray-100 border-gray-300'} focus:outline-none focus:ring-2 focus:ring-blue-500`;

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center px-4 z-50">
      <div className={`w-full max-w-md p-6 rounded-xl shadow-lg ${darkMode ? 'bg-slate-800' : 'bg-white'} max-h-[90vh] overflow-y-auto`}>
        <h3 className="text-xl font-bold mb-4">
          {isEditing ? 'Modifica Tipologia di gara' : 'Aggiungi Tipologia di gara'}
        </h3>

        <form onSubmit={handleSubmit} className="space-y-4">
          <input
            type="text"
            placeholder="Codice (es. 100SL)"
            value={codice}
            onChange={(e) => setCodice(e.target.value)}
            required
            className={`${inputClass} font-mono`}
          />
          <input
            type="text"
            placeholder="Nome (es. 100 stile libero)"
            value={nome}
            onChange={(e) => setNome(e.target.value)}
            required
            className={inputClass}
          />
          <div>
            <label className="block text-xs opacity-60 mb-1">Stile</label>
            <input
              type="text"
              list="stili-gara"
              value={stile}
              onChange={(e) => setStile(e.target.value)}
              required
              className={inputClass}
            />
            <datalist id="stili-gara">
              {[...STILI_GARA, 'mista'].map(s => <option key={s} value={s} />)}
            </datalist>
          </div>
          <div>
            <label className="block text-xs opacity-60 mb-1">Distanza (m)</label>
            <input
              type="number"
              min="1"
              value={distanza}
              onChange={(e) => setDistanza(e.target.value)}
              required
              className={inputClass}
            />
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={staffetta} onChange={(e) => setStaffetta(e.target.checked)} />
            Staffetta
          </label>
          {staffetta && (
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={mistaffetta} onChange={(e) => setMistaffetta(e.target.checked)} />
              Mistaffetta (maschi e femmine)
            </label>
          )}
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={soloVasca25} onChange={(e) => setSoloVasca25(e.target.checked)} />
            Solo vasca da 25 m
          </label>

          <ErrorBox message={error} darkMode={darkMode} />

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onCancel}
              className={`flex-1 px-4 py-3 rounded-lg font-semibold ${darkMode ? 'bg-slate-700 hover:bg-slate-600' : 'bg-gray-200 hover:bg-gray-300'}`}
            >
              Annulla
            </button>
            <button
              type="submit"
              disabled={saving}
              className={`flex-1 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 rounded-lg transition ${saving ? 'opacity-60 cursor-not-allowed' : ''}`}
            >
              {saving ? 'Salvataggio...' : 'Salva'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
